import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ProcessMenu } from '../components/ProcessMenu.js';
import { CustomerAddressPanel } from '../components/CustomerAddressPanel.js';
import {
  PROCESS_MENU_OPTIONS,
  makeProcessMenuHandler,
} from './process-menu-shared.js';
import { api } from '../api/client.js';
import { useCustomer } from '../customer-context.js';
import { useContent } from '../content.js';
import { deriveIssuingBranchCode } from '../lib/issuing-branch.js';
import { formatOwners } from '../lib/owners.js';
import { accountOptionLabel } from '../lib/account-label.js';
import { formatCurrency } from './account-detail-shared.js';
import { CLEAR_CHEQUE_COUNTER } from '../email-templates/templates.js';
import { openTemplateEmail } from '../email-templates/mailto.js';
import { downloadTemplateEml } from '../email-templates/eml.js';
import { resolveActionBlockMessage } from '../lib/action-denied.js';
import {
  requiresMultipleSignatories,
  MULTIPLE_SIGNATORIES_MESSAGE,
} from '../lib/signatories.js';

/**
 * SCRN 40 — Chequebook Menu (CCF-36) + SCRN 41 — Chequebook Order / Stop a
 * Cheque (CCF-37 / CCF-76 / CCF-78).
 *
 * Faithful port of the customer's "update Chequebook" Figma
 * (`requirements/figmas/update-chequebook`). The agent picks an account, sees
 * the account details, then a "Chequebook options" dropdown of two options:
 *   1. Order chequebook  — order eligibility / fees from the Bank API, charge
 *                          scripting, a "Scripting complete" tick reveals the
 *                          Order button which places the order for real.
 *   2. Stopping cheques  — "Stop a cheque" (single) or "Stop cheque range"
 *                          (From/To inclusive), a reason code, charge Yes/No.
 *
 * ORDER CHEQUEBOOK IS WIRED (issue #146): the PTSB R4 `chequebook/enquiry` +
 * `chequebook/order` ops are now contracted and live, so the account list
 * (`/api/customers/accounts`), the order-eligibility / fee panel
 * (`/api/accounts/chequebook-enquiry`) and the Order button
 * (`/api/accounts/chequebook-order`) call the Bank API through the generated
 * typed client, behind the same Teller-ID + RBAC + verification gates as the
 * sibling servicing screens (Statements / Card Maintenance). Gateway
 * outcomes are surfaced INLINE beside the action (#80/#82/#94).
 *
 * STOP A CHEQUE / STOP CHEQUE RANGE ARE NOW WIRED (issue #147): the PTSB R4
 * `cheque/stop` op is contracted (accounts.r4.yaml), so the Stop cheque button
 * calls `/api/accounts/cheque-stop` through the generated typed client — a
 * single stop (cheque number + optional amount) or a range (From/To inclusive),
 * with the reason + charge (Y/N) — behind the same Teller-ID + RBAC +
 * verification + account-ownership gates as Order chequebook. Gateway outcomes
 * are surfaced INLINE beside the action (#80/#82/#94): a decline (e.g. the
 * cheque has already been presented through clearing — CCF-37 unhappy path) maps
 * to the gateway code + message rather than a stubbed success.
 *
 * The shared agent banner lives in the app shell, so this screen renders only
 * the `<main>` content.
 */

/**
 * A card (from `/api/cards` — the R4 `card/list`). The chequebook Secure Address
 * update (CCF-77) commits against the card linked to the selected account, keyed
 * on the card's `CardToken` — the SAME card-based `card/secure-address` op the
 * Card Maintenance journey uses (CCF-77 lists "Order a Chequebook" among the
 * secure-address screens). We read `AccountNumber` to match the card to the
 * selected chequebook account, and `SecureAddress` to seed the field with the
 * persisted flag rather than a hardcoded default (#468).
 */
interface CardRow {
  CardToken: string;
  AccountNumber?: string;
  Status?: string;
  SecureAddress?: string;
}

/** A customer account (from `/api/customers/accounts`). */
interface AccountRow {
  AccountNumber: string;
  AccountType: string;
  Nickname?: string;
  // Account-details figma fields (#497). The `customer/accounts/list` row the BFF
  // normalises already carries these — the Chequebook "Account details" tile must
  // match the customer Figma (IBAN + Available Balance + Owner(s) + Holds), not a
  // free-form list of eligibility flags.
  Iban?: string;
  // Available balance for the account, formatted with the account's own currency
  // symbol (#415 — a foreign-currency account must NOT render as euro).
  AvailableBalance?: number;
  // ISO currency code (e.g. `EUR`, `USD`) driving the balance's symbol (#415).
  Currency?: string;
  // Structured per-account holds (`{ Code }[]`) the BFF derives from
  // `customer/accounts/list`; rendered as the figma "Holds" field (comma-joined
  // codes, "1,2,3 etc").
  Holds?: ReadonlyArray<{ Code?: string }>;
  // Account holder(s): the CCF-80 `Owners` field the BFF overlays onto each
  // `customer/accounts/list` row (`deriveOwners` — "A & B" for a joint account,
  // or an array on some gateway shapes). Used to list ALL owners on the
  // account-details panel (issue #414); absent for a sole-owner account.
  Owners?: string | readonly string[];
  // #471: the per-account `ChequeBookPermittedInd` (Y/N) flag the BFF threads
  // through from `customer/accounts/list`. The account selector below is filtered
  // to ONLY accounts where this is 'Y' (@dylan219) — an account is not offered
  // for chequebook servicing unless the gateway explicitly permits it. An account
  // whose flag is absent / not 'Y' is excluded.
  ChequeBookPermittedInd?: string;
  // #799 (@dylan219): the per-account signatory count the BFF normalises from the
  // gateway `customer/accounts/list` `NumofSignatories` field. When it is > 1 the
  // account requires more than one signature ("two to sign" mandate) and a set of
  // account-changing actions — including Chequebook Order — must NOT be permitted
  // through the desktop; the agent is shown the "Greater than 1 signature required
  // - Check Mandate" block message. May arrive as a number (normalised) or the
  // raw string wire value.
  Signatories?: number | string;
}

/** The chequebook order-eligibility / fee enquiry (`chequebook/enquiry`). */
interface ChequebookEnquiry {
  Found?: boolean;
  FirstTimeOrder?: string;
  AccountTypeEligible?: string;
  ChequeBookOrdered?: string;
  OrderWithinThreshold?: string;
  OutstandingChequesExceeded?: string;
  AccountHold?: string;
  SufficientFunds?: string;
  StampDutyAmt?: string;
  ChequeIssueFee?: string;
  // Returns-in-window ineligibility signals read by `deriveIneligibilityReasons`
  // and the server-side order re-check (#597 cheque returns / #596 DD returns).
  // Declared here so the enquiry flag types match the gateway/BFF wire shape
  // (`DDReturns` / `ChequeReturns`, Y/N) — no type drift.
  ChequeReturns?: string;
  DDReturns?: string;
  NumberChequesOutstanding?: number;
  LastNumberOrdered?: string;
  LastOrderDate?: string;
  // #869 (CCRS-3795/3796, AGW-84) — the chequebook profile on file, pre-filling
  // the order fields. Y/N strings (PostToCustomer may be ''), numeric code /
  // books / cheques counts (ChequeBookCode 0 when none on file).
  IrishChequeBookInd?: string;
  AutomaticOrdering?: string;
  PostToCustomer?: string;
  ChequeBookCode?: number;
  BooksPerOrder?: number;
  ChequesPerBook?: number;
  BranchName?: string;
  BranchAddress1?: string;
  BranchAddress2?: string;
  BranchAddress3?: string;
  ResultCode?: string;
  ResultMessage?: string;
}

// The mandated agent-facing block message for a returns-driven ineligibility
// (#596 DD returns / #597 cheque returns). The gateway's `chequebook/enquiry`
// exposes `DDReturns` / `ChequeReturns` (Y/N — set when there have been Direct
// Debit / cheque returns within the recent window); when either is 'Y' the
// account is NOT eligible to order and the agent is shown this exact verbatim
// advisory (per the bug reports) rather than a free-form reason.
const RETURNS_BLOCK_MESSAGE =
  'The chequebook cannot be ordered at this time due to account status, please check account holds.';

// #538 / #497 — Order-chequebook ineligibility reasons, derived from the live
// `chequebook/enquiry` flags. Each flag is an independent ineligibility signal:
//   - AccountTypeEligible:'N'         → account type not eligible
//   - AccountHold:'Y'                 → an account hold blocks the order
//   - SufficientFunds:'N'             → insufficient funds for the fees
//   - OutstandingChequesExceeded:'Y'  → too many cheques outstanding
//   - DDReturns:'Y'                   → Direct Debit returns in the recent window (#596)
//   - ChequeReturns:'Y'               → cheque returns in the recent window (#597)
//   - OrderWithinThreshold:'Y'        → already ordered within the recent window
// The order is eligible ONLY when every signal is clear. Kept at module scope so
// the render-time badge AND the order-step re-check (#538) share one definition
// and can never drift apart.
//
// #596/#597: `DDReturns`/`ChequeReturns` come from the SAME gateway enquiry
// (`accounts.r4.yaml` documents both) but were previously IGNORED here — so an
// account with DD/cheque returns wrongly reported "Order Eligible: Yes". Both
// now block the order with the mandated `RETURNS_BLOCK_MESSAGE`. The server-side
// re-check (`deriveChequebookIneligibility`, chequebook-order-guard.ts) enforces
// the same two flags so the block cannot be bypassed by a direct API call.
//
// #637 (SCRN-40 / CCF-113): the returns-block advisory copy is admin-editable
// content-catalogue text (`chequebook.order.returns_block`). The screen resolves
// it via `useContent` and passes it in; it defaults to the compiled-in
// `RETURNS_BLOCK_MESSAGE` so this pure helper (and any non-hook caller) still
// renders the baked-in default when no override is supplied. The block LOGIC is
// unchanged — only the literal advisory string is reworded.
function deriveIneligibilityReasons(
  enquiry: ChequebookEnquiry | null,
  returnsBlockMessage: string = RETURNS_BLOCK_MESSAGE,
): string[] {
  if (!enquiry) return [];
  return (
    [
      enquiry.AccountTypeEligible === 'N'
        ? 'Account type is not eligible for a chequebook.'
        : '',
      // #743 (@philipc39): reworded hold reason — point the agent at account holds.
      enquiry.AccountHold === 'Y'
        ? 'The chequebook cannot be ordered at this time due to account status, please check account holds.'
        : '',
      // #743 (@dylan219 field→response table): the insufficient-funds reason
      // displays the mandated agent-facing response verbatim.
      enquiry.SufficientFunds === 'N'
        ? 'The chequebook cannot be ordered at this time due to insufficient funds.'
        : '',
      // #743 (@philipc39): reworded outstanding-cheques reason — the counter is
      // cleared via the standard email template.
      enquiry.OutstandingChequesExceeded === 'Y'
        ? 'The chequebook cannot be ordered at this time. Please clear the chequebook counter using the standard email template.'
        : '',
      enquiry.DDReturns === 'Y' || enquiry.ChequeReturns === 'Y'
        ? returnsBlockMessage
        : '',
      // #743 (@dylan219 field→response table + 11:28Z correction):
      // `OrderWithinThreshold:'Y'` = chequebook ordered WITHIN X DAYS → the
      // mandated response is the clear-the-counter message. Now identical to
      // the outstanding-cheques copy, so the dedupe below collapses it to one
      // bullet when both fire.
      enquiry.OrderWithinThreshold === 'Y'
        ? 'The chequebook cannot be ordered at this time. Please clear the chequebook counter using the standard email template.'
        : '',
    ]
      .filter((r) => r !== '')
      // #743: several reworded reasons are intentionally identical copy pairs
      // (hold ≡ returns-block default; ordered-within-x-days ≡
      // outstanding-cheques) — when both signals fire the "Reason for
      // ineligibility" list renders ONE bullet (the `<li key={reason}>` list
      // below relies on unique strings).
      .filter((reason, index, all) => all.indexOf(reason) === index)
  );
}

/** The two chequebook options (CCF-36 / CCF-37). */
const CHEQUEBOOK_OPTIONS = [
  { value: 'order', label: 'Order chequebook' },
  { value: 'stop', label: 'Stopping cheques' },
] as const;

type ChequebookOption = (typeof CHEQUEBOOK_OPTIONS)[number]['value'] | '';

/**
 * Reason codes for stopping a cheque (CCF-37 / CCF-78). The reason-code dropdown
 * offers Lost/Stolen and Other; the selected label is sent as the gateway
 * `Reason`. The "Charges" option was removed per #507 (bug-reporter) — it is not
 * a valid reason for stopping a cheque on this flow.
 */
const STOP_REASONS = [
  { value: 'lost-stolen', label: 'Lost/Stolen' },
  { value: 'other', label: 'Other' },
] as const;

/**
 * Known stop-cheque decline codes → agent-friendly messages (#147, mirroring the
 * #172 order-decline mapping). The gateway signals a cheque that has already been
 * presented through clearing (CCF-37 unhappy path) with generic copy; surface the
 * real reason while still showing the code. Unknown codes fall back to the generic
 * gateway Code + Message.
 */
const STOP_DECLINE_MESSAGES: Record<string, string> = {
  '153':
    'This cheque has already been presented through clearing and cannot be stopped. Follow the business process.',
};

/**
 * Known chequebook-order decline codes → agent-friendly messages (#172).
 * The gateway returns generic copy (e.g. 9276 "Unable to process Transaction")
 * for an insufficient-funds decline; surface the real reason while still
 * showing the code, mirroring the #167 transfer decline-reason mapping. Unknown
 * codes fall back to the generic gateway Code + Message.
 */
const ORDER_DECLINE_MESSAGES: Record<string, string> = {
  '9276':
    'The Chequebook cannot be ordered at this time due to insufficient funds.',
};

/**
 * #869 (CCRS-3795) — the PTSB cheque-book codes the agent may select, with the
 * number of cheques per book each code carries. Mirrors the BFF catalogue
 * (`server/src/chequebook-order-fields.ts`), which re-validates the code before
 * the gateway is called.
 */
const CHEQUE_BOOK_CODES = [
  { code: 14, label: 'Euro - Side Stub', chequesPerBook: 40 },
  { code: 16, label: 'Euro Company - 50', chequesPerBook: 50 },
  { code: 18, label: 'Euro - As Gaeilge', chequesPerBook: 20 },
  { code: 20, label: 'Euro Business Cheques', chequesPerBook: 50 },
] as const;

type ChequeBookCode = (typeof CHEQUE_BOOK_CODES)[number]['code'];

/** Books per order bounds (gateway `BooksPerOrder` 1–99, CCRS-3796). */
const BOOKS_PER_ORDER_MAX = 99;

type YesNo = '' | 'Y' | 'N';

/**
 * #869 — the agent-confirmable order fields, held as form strings. `''` means
 * "not selected": a value the enquiry returned that is not a valid choice (a
 * code outside the catalogue such as 0, or a PostToCustomer of '') is NEVER
 * defaulted — the drop-down stays unselected and Order is blocked until the
 * agent chooses.
 */
interface OrderFields {
  postToCustomer: YesNo;
  chequeBookCode: string;
  automaticOrdering: YesNo;
  irishChequeBookInd: YesNo;
  booksPerOrder: string;
}

const EMPTY_ORDER_FIELDS: OrderFields = {
  postToCustomer: '',
  chequeBookCode: '',
  automaticOrdering: '',
  irishChequeBookInd: '',
  booksPerOrder: '',
};

function toYesNo(value: unknown): YesNo {
  return value === 'Y' || value === 'N' ? value : '';
}

/** Pre-fill the order fields from the enquiry (CCRS-3795/3796). */
function orderFieldsFromEnquiry(
  enquiry: ChequebookEnquiry | null,
): OrderFields {
  if (!enquiry) return EMPTY_ORDER_FIELDS;
  const code = CHEQUE_BOOK_CODES.find((c) => c.code === enquiry.ChequeBookCode);
  const books = enquiry.BooksPerOrder;
  return {
    postToCustomer: toYesNo(enquiry.PostToCustomer),
    chequeBookCode: code ? String(code.code) : '',
    automaticOrdering: toYesNo(enquiry.AutomaticOrdering),
    irishChequeBookInd: toYesNo(enquiry.IrishChequeBookInd),
    booksPerOrder:
      typeof books === 'number' &&
      Number.isInteger(books) &&
      books >= 1 &&
      books <= BOOKS_PER_ORDER_MAX
        ? String(books)
        : '',
  };
}

/** The labels of the order fields still needing an agent choice / value. */
function missingOrderFields(fields: OrderFields): string[] {
  const missing: string[] = [];
  if (!fields.postToCustomer) missing.push('Post to customer');
  if (!fields.chequeBookCode) missing.push('Chequebook code');
  if (!fields.automaticOrdering) missing.push('Automatic ordering');
  if (!fields.irishChequeBookInd) missing.push('Irish chequebook');
  const books = Number(fields.booksPerOrder);
  if (
    !fields.booksPerOrder ||
    !Number.isInteger(books) ||
    books < 1 ||
    books > BOOKS_PER_ORDER_MAX
  ) {
    missing.push('Books per order (1–99)');
  }
  return missing;
}

/** BFF 400 codes for a refused order field (#869) → agent-facing copy. */
const ORDER_FIELD_ERROR_MESSAGES: Record<string, string> = {
  invalid_irish_chequebook_ind: 'Select Y or N for Irish chequebook.',
  invalid_automatic_ordering: 'Select Y or N for Automatic ordering.',
  invalid_post_to_customer: 'Select Y or N for Post to customer.',
  invalid_cheque_book_code: 'Select a chequebook code.',
  invalid_books_per_order: 'Enter a number of books per order from 1 to 99.',
};

/*
 * A chequebook was already ordered within the gateway's recent-order window
 * (#171). The enquiry reports this via `OrderWithinThreshold` (Y), so we
 * suppress the Order action and show an inline advisory rather than letting the
 * agent place repeat orders within the threshold window. #743 (@dylan219
 * field→response table + 11:28Z correction): `OrderWithinThreshold:'Y'` means
 * a chequebook was ordered WITHIN X DAYS and the mandated response is the
 * clear-the-counter message (shown both as the inline advisory and the reason
 * bullet).
 *
 * `ChequeBookOrdered:'Y'` is NOW ALSO a block (#714 — supersedes #353):
 * @dylan219's ruling in #714 is that `ChequeBookOrdered:'Y'` means a chequebook
 * has already been ordered on the account and the agent must be blocked. The
 * previous #353 interpretation (a lifetime/eligibility flag that fires on most
 * active accounts and should not block) is superseded. The screen and the
 * server-side guard both enforce this. Per @dylan219's 11:28Z correction on
 * #743, `ChequeBookOrdered:'Y'` is the chequebook-ordered-TODAY signal: its
 * display copy is the admin-editable content-catalogue text
 * (`chequebook.order.already_ordered`, #637), resolved in the component via
 * `useContent` with the compiled-in default "Chequebook already ordered today.
 * Please try again tomorrow." as its fallback (see `alreadyOrderedMessage`) —
 * the block itself is unchanged.
 */

/** Accessible dropdown, faithful to the export's listbox pattern (shared with
 *  the sibling servicing screens — see StatementsScreen). */
function Dropdown({
  id,
  label,
  value,
  placeholder,
  options,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  placeholder: string;
  options: readonly { value: string; label: string }[];
  onChange: (value: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const selected = options.find((o) => o.value === value);

  useEffect(() => {
    function onClickOutside(e: MouseEvent): void {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    }
    if (open) {
      document.addEventListener('mousedown', onClickOutside);
      return () => document.removeEventListener('mousedown', onClickOutside);
    }
    return undefined;
  }, [open]);

  return (
    <div className="flex flex-col gap-2" ref={ref}>
      <label
        htmlFor={id}
        className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
      >
        {label}
      </label>
      <div className="relative">
        <button
          id={id}
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-haspopup="listbox"
          aria-expanded={open}
          className="w-full px-4 py-3 border-2 border-[#dad7d3] rounded-md font-['Arial',sans-serif] text-[16px] text-[#1d252c] bg-white text-left transition-all focus:outline-none focus:border-[#005fcc] focus:ring-4 focus:ring-[#005fcc]/20 hover:border-[#979c9f] flex items-center justify-between"
        >
          <span className={selected ? '' : 'text-[#6b7280]'}>
            {selected ? selected.label : placeholder}
          </span>
          <svg
            className={`size-5 transition-transform ${open ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            aria-label={label}
            className="absolute top-full mt-2 w-full bg-white border-2 border-[#dad7d3] rounded-md shadow-lg z-[100] max-h-[240px] overflow-y-auto"
          >
            {options.map((option) => (
              <li
                key={option.value || 'placeholder'}
                role="option"
                aria-selected={value === option.value}
                tabIndex={0}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onChange(option.value);
                    setOpen(false);
                  }
                }}
                className={`px-4 py-3 cursor-pointer font-['Arial',sans-serif] text-[16px] text-[#1d252c] hover:bg-[#f3f4f6] focus:outline-none focus:bg-[#e5e7eb] ${
                  value === option.value ? 'bg-[#e5e7eb] font-bold' : ''
                }`}
              >
                {option.label}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

/** A read-only labelled field used in the account-details grid. */
function DetailField({
  label,
  value,
}: {
  label: string;
  value: string;
}): React.JSX.Element {
  return (
    <div>
      <p className="text-[14px] font-['Arial',sans-serif] text-[#6b7280] mb-1 m-0">
        {label}
      </p>
      <p className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0">
        {value}
      </p>
    </div>
  );
}

export function ChequebookScreen(): React.JSX.Element {
  const { customer } = useCustomer();
  const navigate = useNavigate();

  // #637 (SCRN-40 / CCF-113) — the order-blocked advisory copy is admin-editable
  // content-catalogue text (non-regulatory guidance); each falls back to its
  // compiled-in default so the screen renders unchanged when there is no
  // override. Rendered as PLAIN TEXT only (no HTML).
  const returnsBlockMessage = useContent('chequebook.order.returns_block');
  const alreadyOrderedMessage = useContent('chequebook.order.already_ordered');

  const customerNumber = customer.cif ?? '';
  const tellerId = customer.tellerId ?? '';
  const hasContext = !!customerNumber && /^\d{4}$/.test(tellerId);

  // Shared process-menu navigation handler (issue #45) — wires each option to
  // its existing, RBAC-gated route.
  const onProcessSelect = makeProcessMenuHandler(navigate);

  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [accountsError, setAccountsError] = useState('');
  const [account, setAccount] = useState('');
  const [option, setOption] = useState<ChequebookOption>('');
  // Mirrors `account` synchronously (issue #337): `fetchEnquiry` is now called
  // from two places (the account-selection effect AND the post-order refresh)
  // and needs to discard a late response for an account the agent has since
  // navigated away from, without re-introducing the effect-cancellation race
  // class (#244) — a plain ref read at apply-time is enough here since a
  // stale write is simply dropped, never acted on.
  const accountRef = useRef('');

  // Issue #169 — the CIF + Postal addresses shown on the Order chequebook panel
  // are sourced from the gateway (NOT hardcoded): the CIF address from the
  // customer dashboard detail set (`CustomerAddressLine1..5` + `CustomerEircode`)
  // and the account's postal/delivery address from `/accounts/postal-address`
  // (`getPostalAddress` — the same per-account source SCRN-23 / Statements use,
  // granted to exactly the chequebook role set). Held as their five lines for the
  // shared CustomerAddressPanel. Loading is non-fatal — a column renders blank
  // rather than placeholder text when nothing is on file.
  const [cifAddress, setCifAddress] = useState<string[]>([]);
  const [cifEircode, setCifEircode] = useState('');
  const [postalAddress, setPostalAddress] = useState<string[]>([]);
  // Customer type for the account-details tile (#497 — the figma shows
  // "Customer type: Personal"). #770: now sourced from the dashboard
  // `info.CustomerType` (the raw customer type / `CustDescription` from
  // customer/identification — the SAME field the Dashboard "Customer type" tile
  // now reads), loaded alongside the CIF address below — NOT the normalised
  // routing Segment.
  const [customerType, setCustomerType] = useState('');

  // The customer's cards (#468) — the Secure Address update commits against the
  // card linked to the selected account (`card/secure-address`, keyed on
  // `CardToken`), so we load `card/list` once and resolve the relevant card per
  // account. Non-fatal: if it can't load, the Update surfaces a clear inline
  // error rather than a false success.
  const [cards, setCards] = useState<CardRow[]>([]);
  // Load status of the `/cards` read (#468 ai-review HIGH). The card list drives
  // `linkedCard`, which the Secure Address flag read + Update commit against.
  // `'idle'` before anything runs, `'loading'` while the read is in flight (and
  // whenever the customer/teller context changes — the previous customer's cards
  // are cleared FIRST so `linkedCard` can never resolve against a stale card from
  // a prior context), `'loaded'` on a successful read, `'error'` if the read
  // failed. This lets the UI distinguish "cards still loading / failed to load"
  // (Update disabled, do not claim there is no linked card) from a confirmed
  // empty list (genuinely no linked card).
  const [cardsStatus, setCardsStatus] = useState<
    'idle' | 'loading' | 'loaded' | 'error'
  >('idle');
  // Retry nonce for the `/cards` read itself (#468 ai-review MEDIUM). When the
  // `/cards` read fails, `cards` is empty so no card resolves and the Secure
  // Address seeding effect surfaces `secureFlagStatus === 'error'` with a Retry
  // control. That Retry used to only bump `secureFlagReload`, which re-runs the
  // per-card flag read — but with `cardsStatus === 'error'` and no linked card
  // that effect just returns to `'error'` without ever re-running the `/cards`
  // loader, so the agent was stuck behind a non-functional Retry. Bumping this
  // nonce (which the `/cards` loader depends on) re-triggers the cards read so a
  // transient `/cards` failure can actually be recovered.
  const [cardsReload, setCardsReload] = useState(0);

  // Order chequebook (CCF-36) — eligibility/fees + order state.
  const [enquiry, setEnquiry] = useState<ChequebookEnquiry | null>(null);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [enquiryError, setEnquiryError] = useState('');
  // The Secure Address flag. Starts EMPTY (unknown), never a hardcoded 'Y'
  // (#468 ai-review HIGH). The authoritative persisted value is fetched from
  // `/cards/postal-address` (the R4 `card/postal-address` op — the SAME source
  // Card Maintenance seeds from and the authoritative flag per #154); the real
  // R4 `card/list` does NOT always carry SecureAddress, so seeding from it would
  // show 'Y' when the persisted value may be 'N' and let an agent submit an
  // unintended Y→N reversal. Until the authoritative flag loads, the field shows
  // an unknown state and the Update button is disabled (see `secureFlagStatus`).
  const [secureAddress, setSecureAddress] = useState('');
  // Load status of the AUTHORITATIVE persisted flag from `/cards/postal-address`
  // (#468). `'idle'` before anything runs, `'no-card'` when no card is linked to
  // the account (Update cannot apply here), `'loading'` while the read is in
  // flight, `'loaded'` once a valid persisted Y/N flag is seeded, `'error'` if
  // the read failed OR returned no valid Y/N value. The Update button is disabled
  // unless the flag is `'loaded'` AND the agent has resolved it to a valid Y/N —
  // so a defaulted/unknown/stale value can never be submitted over a real
  // persisted flag (#468 ai-review HIGH).
  const [secureFlagStatus, setSecureFlagStatus] = useState<
    'idle' | 'no-card' | 'ambiguous' | 'loading' | 'loaded' | 'error'
  >('idle');
  // Retry nonce for the AUTHORITATIVE Secure Address flag read (#468 ai-review
  // MEDIUM). The seeding effect below is keyed on the linked card token — so a
  // transient read failure could NOT be retried for the SAME card: reselecting
  // the same account / reopening the panel did not change the token, so the
  // effect never re-fired and the Update control stayed permanently disabled
  // after a blip. Bumping this nonce (via the inline Retry control) forces the
  // effect to re-run and re-read the persisted flag for the same card.
  const [secureFlagReload, setSecureFlagReload] = useState(0);
  // Has the agent edited the Secure Address field in this session (#468)? The
  // persisted flag from `/cards/postal-address` seeds the field, but only while
  // the agent has NOT touched it — a dirty field is never re-seeded, so a slow
  // response arriving AFTER the agent changed the value can no longer clobber
  // their in-progress choice (the previous seed keyed on `secureResult == null`,
  // which the very act of editing also cleared, so the late load overwrote the
  // edit). A ref, not state: it must be read at seed-time without itself
  // re-triggering the seeding effect. Reset to false on account change / panel
  // reset (so the new card's persisted flag re-seeds) and after a confirmed
  // successful update (so the confirmed value becomes the new baseline).
  const secureAddressDirty = useRef(false);
  // The last AUTHORITATIVE persisted Secure Address flag for the resolved card
  // (#468 ai-review HIGH). Set when the authoritative read seeds the field, and
  // updated to the gateway-confirmed value after a successful commit; cleared
  // when no valid flag is known (no card / read error / fresh card load).
  // `resetOrder` restores `secureAddress` from this baseline so an UNSAVED edit
  // can never survive a same-card panel reset/reopen and be treated as the
  // current persisted value — the seeding effect is keyed only on the card
  // token, so it does NOT re-read on a same-card reset. `''` means the flag is
  // not yet known, so a reset then restores the unknown state (Update disabled).
  const securePersistedFlag = useRef('');
  // The card token the authoritative `securePersistedFlag`/`secureFlagStatus` were
  // loaded FOR (#468 ai-review HIGH). `secureFlagStatus` is state and only becomes
  // `'loading'` when the seeding effect re-runs (a tick AFTER an account/card
  // switch), so between switching account A→B and that effect firing there is a
  // render window where account B is shown but the status is still A's `'loaded'`
  // — the Update button would be enabled and could POST account A's stale flag
  // against account B's linked card token. Gating the button on this token
  // matching the CURRENT `linkedCard.CardToken` closes that window synchronously:
  // on an account/card change `linkedCard.CardToken` changes immediately (derived
  // from `account` state) while this ref still holds the previous card's token, so
  // Update is disabled until the effect re-seeds it for the new card. A ref, not
  // state: it is written alongside `securePersistedFlag`/`secureFlagStatus` and
  // read at render-time; the status/`linkedCard` state changes already re-render.
  const securePersistedToken = useRef('');
  // Inline result of the Secure Address Update action, surfaced beside the button
  // (#80/#82/#94 — CCF-77 Unhappy Path #1: an error on Update is shown with a
  // retry, never a fleeting/false success). Replaces the previous optimistic
  // toast that claimed success without ever calling the gateway (#468).
  const [secureUpdating, setSecureUpdating] = useState(false);
  const [secureResult, setSecureResult] = useState<{
    ok: boolean;
    message: string;
  } | null>(null);
  const [orderScripting, setOrderScripting] = useState(false);
  // #869 (CCRS-3795/3796) — the order fields, pre-filled from the enquiry when
  // the account loads (and on re-opening the order option) and amendable by the
  // agent. Cleared on an account / customer change so a previous selection is
  // never carried across.
  const [orderFields, setOrderFields] =
    useState<OrderFields>(EMPTY_ORDER_FIELDS);
  const [ordering, setOrdering] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [orderSuccess, setOrderSuccess] = useState('');

  // Stopping cheques (CCF-37) state.
  const [stopType, setStopType] = useState<'single' | 'range'>('single');
  const [chequeNumber, setChequeNumber] = useState('');
  const [chequeFrom, setChequeFrom] = useState('');
  const [chequeTo, setChequeTo] = useState('');
  const [chequeAmount, setChequeAmount] = useState('');
  const [reason, setReason] = useState('');
  const [charge, setCharge] = useState<'' | 'Yes' | 'No'>('');
  const [stopScripting, setStopScripting] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState('');
  const [stopSuccess, setStopSuccess] = useState('');
  // Inline mandatory-field warnings for the Stop a cheque flow (#508, #607). The
  // single-cheque "Cheque number" (single mode), the "From"/"To" range fields
  // (range mode), and the "Reason for stopping" dropdown are mandatory — an empty
  // required field surfaces a recoverable inline warning beside the control and
  // BLOCKS the stop-cheque submit rather than letting the request proceed with a
  // missing value (#80/#82/#94 inline-error UX). Keyed per-field so each empty
  // field shows its own message.
  const [stopFieldErrors, setStopFieldErrors] = useState<{
    chequeNumber?: string;
    chequeFrom?: string;
    chequeTo?: string;
    reason?: string;
  }>({});

  const ownerName = customer.name || '';

  // Account holder(s) for the details panel (issue #414 — "where owner is shown
  // we always show all owners"). A joint account must list ALL holders, not
  // just the single verified in-context customer. Source is the CCF-80 `Owners`
  // field the BFF overlays onto each `customer/accounts/list` row (the same
  // source Account Information (#414 incr. 1) / Dashboard (#11) / Historic
  // Transactions (#382) read), comma-formatted via the shared `formatOwners`
  // helper; falls back to the verified banner customer name when the selected
  // account carries no owner data. (`ownerName` above stays the single
  // in-context customer for the prefilled email draft — the person on the call.)
  const selectedAccountRow = accounts.find((a) => a.AccountNumber === account);
  const accountOwners = formatOwners(selectedAccountRow?.Owners) || ownerName;

  // #799 (@dylan219): the selected account requires MORE THAN ONE signatory
  // ("two to sign" mandate) — derived from the gateway `NumofSignatories` count
  // the BFF threads onto each account. When true, Chequebook Order AND (increment
  // 3) Stopping Cheque(s) are BLOCKED (the gateway is never called) and the agent
  // is shown the verbatim "Greater than 1 signature required - Check Mandate"
  // message inline beside the action, so they can pick a different account
  // instead. Shared with the other blocked action-areas via
  // `requiresMultipleSignatories`.
  const selectedAccountMultiSignatory =
    requiresMultipleSignatories(selectedAccountRow);

  // The card linked to the selected account (#468) — the Secure Address update is
  // a STATE-CHANGING banking write committed against a specific `CardToken`, so
  // the target card must be UNAMBIGUOUS. Resolve a single card ONLY when the
  // choice is unambiguous: exactly one ACTIVE card on the account, or (when none
  // is active) a single card on the account. If the account carries MORE THAN ONE
  // eligible card we deliberately do NOT guess — `linkedCard` stays `undefined`
  // and `secureCardAmbiguous` drives a fail-closed inline error, rather than
  // silently updating whichever card happens to appear first in gateway order
  // (#468 ai-review HIGH). `undefined` also covers "no card matches" (e.g. a
  // deposit-only account), which the Update handler surfaces as a clear inline
  // error rather than a false success.
  const accountCards = cards.filter((c) => c.AccountNumber === account);
  const activeAccountCards = accountCards.filter((c) => c.Status === 'ACTIVE');
  const linkedCard =
    activeAccountCards.length === 1
      ? activeAccountCards[0]
      : activeAccountCards.length === 0 && accountCards.length === 1
        ? accountCards[0]
        : undefined;
  // The account HAS card(s) but none resolves to a single unambiguous target
  // (multiple active, or several inactive with none active). Fail-closed: the
  // agent must pick the specific card in Card Maintenance (#468 ai-review HIGH).
  const secureCardAmbiguous = !linkedCard && accountCards.length > 0;

  // Is the authoritative Secure Address flag loaded AND still for the card in
  // view (#468 ai-review HIGH)? `secureFlagStatus` is state that only flips to
  // `'loading'` a render AFTER an account/card switch, so on its own it leaves a
  // window where account B renders with account A's stale `'loaded'` flag and the
  // Update button enabled. Requiring the loaded baseline's token to equal the
  // CURRENT `linkedCard.CardToken` closes that window synchronously: the token
  // changes with `account` state (so the mismatch shows in the same commit as the
  // switch) while the ref only updates once the seeding effect re-reads for the
  // new card. Every enablement of the state-changing commit goes through this.
  const secureFlagReady =
    secureFlagStatus === 'loaded' &&
    !!linkedCard &&
    securePersistedToken.current === linkedCard.CardToken;

  // Load the customer's accounts for the selector (CCF-36).
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      if (!hasContext) {
        setAccountsError(
          'No verified customer in context. Complete ID&V first.',
        );
        return;
      }
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/accounts',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          setAccountsError('Could not load accounts.');
          return;
        }
        setAccountsError('');
        setAccounts((data.Accounts ?? []) as unknown as AccountRow[]);
      } catch {
        if (!cancelled) setAccountsError('Request failed.');
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // Load the customer's cards (#468) so the Secure Address update can commit
  // against the card linked to the selected account (`card/secure-address` is
  // keyed on `CardToken`). Same `/cards` read Card Maintenance uses; the READ is
  // granted to the chequebook role set (`listCards` — SLICE == CHEQUEBOOK roles).
  // Non-fatal: an empty list simply means the Update surfaces an inline error.
  useEffect(() => {
    let cancelled = false;
    // Clear the previous context's cards FIRST (#468 ai-review HIGH): the loader
    // below only replaces `cards` on a SUCCESSFUL read, so without this a stale
    // card array from a prior customer/teller would survive a context change (or
    // a failed/no-data read) and `linkedCard` could resolve against it — driving
    // `/cards/postal-address` / `/cards/secure-address` with a card token from a
    // previous context. Resetting to an empty list + `'loading'` here means the
    // list is never carried across contexts and the UI shows "loading", not a
    // false "no linked card", until the fresh read confirms it.
    setCards([]);
    if (!hasContext) {
      setCardsStatus('idle');
      return;
    }
    setCardsStatus('loading');
    async function loadCards(): Promise<void> {
      try {
        const { data, error: apiError } = await api.POST('/cards', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber },
        });
        if (cancelled) return;
        if (apiError || !data) {
          // Read failed: leave `cards` empty (never a stale array) and mark the
          // load errored so the UI can distinguish it from a confirmed empty
          // list (#468 ai-review HIGH).
          setCardsStatus('error');
          return;
        }
        setCards((data.Cards ?? []) as unknown as CardRow[]);
        setCardsStatus('loaded');
      } catch {
        // Non-fatal — Update surfaces an inline error when no card resolves.
        if (!cancelled) setCardsStatus('error');
      }
    }
    void loadCards();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, cardsReload]);

  // Issue #169 — load the customer's CIF address on file so the Order panel
  // shows the real address (not placeholder text). Reuses the dashboard detail
  // set (the same source Personal Details / Statements read). Non-fatal.
  useEffect(() => {
    let cancelled = false;
    async function loadCifAddress(): Promise<void> {
      if (!hasContext) return;
      try {
        const { data, error: apiError } = await api.POST(
          '/customers/dashboard',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber },
          },
        );
        if (cancelled || apiError || !data) return;
        const info = (data.info ?? {}) as Record<string, unknown>;
        const str = (v: unknown): string =>
          typeof v === 'string' ? v.trim() : '';
        setCifAddress([
          str(info.CustomerAddressLine1),
          str(info.CustomerAddressLine2),
          str(info.CustomerAddressLine3),
          str(info.CustomerAddressLine4),
          str(info.CustomerAddressLine5),
        ]);
        setCifEircode(str(info.CustomerEircode));
        // #497 / #770 — the customer type for the account-details tile
        // ("Personal" / "Signatory" …), the same `CustomerType` field
        // (raw CustDescription) the Dashboard "Customer type" tile renders.
        setCustomerType(str(info.CustomerType));
      } catch {
        // Non-fatal — the CIF column renders blank lines when absent.
      }
    }
    void loadCifAddress();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId]);

  // Fetch the chequebook order-eligibility / fees / outstanding-cheque enquiry
  // for the given account and apply it to `enquiry` state. Shared by the
  // account-selection effect below AND by `onOrderChequebook` (issue #337): a
  // successful order changes the gateway's `LastNumberOrdered`/`LastOrderDate`/
  // `ChequeBookOrdered`/`OrderWithinThreshold` fields, so the "Account details"
  // tile must re-run this same enquiry immediately after ordering rather than
  // continue to show the pre-order snapshot until the agent navigates away and
  // back (which triggered a fresh fetch anyway — the tile was simply never
  // invalidated after the order itself completed).
  // `reportError` is false for the post-order background refresh (issue #337):
  // a transient failure to re-fetch must not blank an already-rendered "Account
  // details" tile with an error box — it just leaves the (now possibly stale)
  // last-known-good data in place rather than compounding a successful order
  // with a spurious inline error.
  // Returns the fresh enquiry on success (so callers — e.g. the #538 order-step
  // re-check — can re-derive eligibility from the up-to-date snapshot rather
  // than a possibly-stale render value), or `null` on any failure / stale race.
  const fetchEnquiry = useCallback(
    async (
      forAccount: string,
      reportError = true,
    ): Promise<ChequebookEnquiry | null> => {
      if (!hasContext || !forAccount) return null;
      try {
        const {
          data,
          error: apiError,
          response,
        } = await api.POST('/accounts/chequebook-enquiry', {
          params: { header: { 'X-Teller-Id': tellerId } },
          body: { customerNumber, accountNumber: forAccount },
        });
        // The agent may have selected a different account while this request
        // was in flight — discard a late response for an account that is no
        // longer the one in view rather than let it clobber current state.
        if (accountRef.current !== forAccount) return null;
        if (apiError || !data) {
          if (reportError) {
            setEnquiryError(
              response.status === 403
                ? 'Forbidden: check your Teller ID and permissions.'
                : `Could not load chequebook details (${response.status}).`,
            );
          }
          return null;
        }
        const result = data as unknown as ChequebookEnquiry;
        if (result.Found === false) {
          if (reportError) {
            setEnquiryError(
              `Could not load chequebook details${
                result.ResultCode ? ` (${result.ResultCode})` : ''
              }${result.ResultMessage ? `: ${result.ResultMessage}` : '.'}`,
            );
          }
          return null;
        }
        setEnquiryError('');
        setEnquiry(result);
        return result;
      } catch {
        if (reportError && accountRef.current === forAccount) {
          setEnquiryError('Request failed.');
        }
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [hasContext, customerNumber, tellerId],
  );

  // Load chequebook order eligibility whenever an account is selected.
  useEffect(() => {
    let cancelled = false;
    async function load(): Promise<void> {
      // #869 — a customer / account change (or a cleared session) wipes any
      // order-field selection before anything else.
      setOrderFields(EMPTY_ORDER_FIELDS);
      if (!hasContext || !account) return;
      setEnquiryLoading(true);
      setEnquiry(null);
      setEnquiryError('');
      setPostalAddress([]);
      const ok = await fetchEnquiry(account);
      if (cancelled) return;
      setEnquiryLoading(false);
      if (!ok) return;
      // #869 — pre-fill the order fields from the freshly-loaded enquiry.
      setOrderFields(orderFieldsFromEnquiry(ok));
      // Issue #169 — load the account's postal/delivery address on file (the
      // same per-account `getPostalAddress` source SCRN-23 / Statements use)
      // so the Order panel shows the real address bound to the selected
      // account. Non-fatal: the column stays blank when none is on file.
      try {
        const { data: addrData, error: addrError } = await api.POST(
          '/accounts/postal-address',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber, accountNumber: account },
          },
        );
        if (cancelled || addrError || !addrData) return;
        const addr = addrData as {
          Found?: boolean;
          Line1?: string;
          Line2?: string;
          Line3?: string;
          Line4?: string;
          Line5?: string;
        };
        if (!addr.Found) {
          setPostalAddress([]);
          return;
        }
        setPostalAddress(
          [addr.Line1, addr.Line2, addr.Line3, addr.Line4, addr.Line5].map(
            (v) => (typeof v === 'string' ? v.trim() : ''),
          ),
        );
      } catch {
        // Non-fatal — the postal column renders blank when it can't load.
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerNumber, tellerId, account]);

  function resetOrder(): void {
    // Restore the Secure Address field to the last AUTHORITATIVE persisted flag
    // (#468 ai-review HIGH). Do NOT touch `secureFlagStatus` here: it is owned by
    // the authoritative-flag fetch effect below (keyed on the linked card's
    // token), and clearing it would race that effect — resetOrder runs on every
    // panel open but the effect only re-fires when the card token changes, so a
    // reset-to-idle would strand the field disabled. But we MUST revert
    // `secureAddress`: because the effect does not re-run on a same-card reset,
    // an UNSAVED edit left in place would remain displayed with Update enabled
    // and be treated as the current persisted value. Restoring from the baseline
    // ref reverts any in-progress change without a re-read (`''` = flag not yet
    // known → unknown state). The field never defaults to 'Y' (#468 ai-review).
    secureAddressDirty.current = false;
    setSecureAddress(securePersistedFlag.current);
    setSecureResult(null);
    setSecureUpdating(false);
    setOrderScripting(false);
    setOrderError('');
    setOrderSuccess('');
    setOrdering(false);
    // #869 — re-opening the order option restores the values on file.
    setOrderFields(orderFieldsFromEnquiry(enquiry));
  }

  // Seed the Secure Address field from the AUTHORITATIVE persisted flag once the
  // linked card resolves (#468 ai-review HIGH). The persisted Y/N flag is read
  // from `/cards/postal-address` (the R4 `card/postal-address` op) — the SAME
  // authoritative source Card Maintenance seeds from, and authoritative for the
  // Y/N indicator per #154. The real R4 `card/list` does NOT always carry
  // SecureAddress, so we deliberately do NOT seed from `linkedCard.SecureAddress`
  // (that risked showing 'Y' when the persisted value was 'N', letting an agent
  // submit an unintended Y→N reversal). While the read is in flight the field is
  // unknown and the Update button is disabled (`secureFlagStatus`). Only seeds
  // while the agent has NOT edited the field (`secureAddressDirty` false), so a
  // slow response arriving after the agent changed the value can never clobber
  // their in-progress choice.
  useEffect(() => {
    let cancelled = false;
    const cardToken = linkedCard?.CardToken;
    if (!hasContext || !cardToken) {
      // No card token resolved. Clear any previously-loaded flag so a stale value
      // from a prior card can never be carried over (#468 ai-review HIGH).
      setSecureAddress('');
      secureAddressDirty.current = false;
      // No authoritative flag for this (absent) card — drop any prior baseline so
      // a reset can't restore a stale flag from a previous card (#468 HIGH), and
      // drop the token it was loaded for so the Update-enable guard can't match.
      securePersistedFlag.current = '';
      securePersistedToken.current = '';
      // Distinguish "cards still loading / failed to load" from a confirmed empty
      // list (#468 ai-review HIGH): only claim `'no-card'` once the `/cards` read
      // has confirmed there is genuinely no linked card. While cards are loading
      // show `'loading'`; if the cards read errored, surface `'error'` (with the
      // Retry control) rather than falsely telling the agent no card is linked.
      if (!hasContext) {
        setSecureFlagStatus('idle');
      } else if (cardsStatus === 'loading' || cardsStatus === 'idle') {
        setSecureFlagStatus('loading');
      } else if (cardsStatus === 'error') {
        setSecureFlagStatus('error');
      } else if (secureCardAmbiguous) {
        // The account has cards but the target is ambiguous (#468 ai-review
        // HIGH): fail-closed rather than silently updating an arbitrary card.
        setSecureFlagStatus('ambiguous');
      } else {
        setSecureFlagStatus('no-card');
      }
      return;
    }
    async function loadSecureFlag(cardToken: string): Promise<void> {
      // Entering a fresh load for a (possibly different) card: clear any value
      // seeded for the previous card and its dirty flag, so the read below is the
      // sole authority for THIS card and a stale Y/N can never be submitted for a
      // newly-selected card (#468 ai-review HIGH). The Update button is disabled
      // while `'loading'`.
      setSecureAddress('');
      secureAddressDirty.current = false;
      // A (possibly different) card is being read: its authoritative flag is not
      // yet known, so clear the previous card's baseline (and the token it was
      // loaded for) until the read seeds a fresh one (#468 HIGH). Update stays
      // disabled while status is not 'loaded' AND the loaded token doesn't match.
      securePersistedFlag.current = '';
      securePersistedToken.current = '';
      setSecureFlagStatus('loading');
      try {
        const { data, error: apiError } = await api.POST(
          '/cards/postal-address',
          {
            params: { header: { 'X-Teller-Id': tellerId } },
            body: { customerNumber, cardToken },
          },
        );
        if (cancelled) return;
        if (apiError || !data) {
          // Authoritative read failed: keep the field unknown and DISABLE Update
          // rather than defaulting to a value that could reverse the persisted
          // flag (#468). The agent can reselect the account to retry.
          setSecureFlagStatus('error');
          return;
        }
        const result = data as unknown as { SecureAddress?: string };
        const flag = result.SecureAddress;
        if (flag !== 'Y' && flag !== 'N') {
          // The authoritative read succeeded transport-wise but carried no valid
          // Y/N SecureAddress (missing field / mapper or schema drift). Treat this
          // as an ERROR — NOT `'loaded'` — so the Update button stays disabled and
          // no stale/defaulted value can be submitted (#468 ai-review HIGH).
          setSecureFlagStatus('error');
          return;
        }
        // Record the authoritative baseline so a later same-card panel reset can
        // revert an unsaved edit back to it without a re-read (#468 HIGH), keyed
        // to the token it was loaded for so the Update-enable guard only trusts it
        // while it still matches the current linked card (#468 ai-review HIGH).
        securePersistedFlag.current = flag;
        securePersistedToken.current = cardToken;
        if (!secureAddressDirty.current) {
          setSecureAddress(flag);
        }
        setSecureFlagStatus('loaded');
      } catch {
        if (!cancelled) setSecureFlagStatus('error');
      }
    }
    void loadSecureFlag(cardToken);
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    linkedCard?.CardToken,
    customerNumber,
    tellerId,
    cardsStatus,
    secureCardAmbiguous,
    secureFlagReload,
  ]);

  // Commit the Secure Address flag against the account's card (CCF-77, #468).
  // Calls `POST /cards/secure-address` (the R4 `card/secure-address` op) and
  // surfaces the outcome inline beside the Update button (#80/#82/#94): success
  // ONLY when the gateway confirms it (`Updated: true`), otherwise the gateway
  // Code + Message. Replaces the previous optimistic toast that claimed success
  // without ever calling the gateway.
  async function onUpdateSecureAddress(): Promise<void> {
    if (!hasContext || !account) return;
    // Constrain the flag to the only two valid gateway values. An out-of-range
    // value (e.g. a typo like `X`) is rejected inline and NO gateway call fires
    // — it is never silently coerced to `Y`, which would be a real, unintended
    // state change (#468 ai-review).
    if (secureAddress !== 'Y' && secureAddress !== 'N') {
      setSecureResult({
        ok: false,
        message: 'Secure address must be Y or N.',
      });
      return;
    }
    const flag = secureAddress;
    // Fail closed on a state-changing banking write: never POST unless the
    // authoritative flag is loaded FOR the card currently in view (#468 ai-review
    // HIGH). The button is already gated on `secureFlagReady`, but re-assert it
    // here so a stale flag from a just-switched account can never reach the
    // gateway even in the brief render window before the seeding effect re-runs.
    if (!secureFlagReady) {
      setSecureResult({
        ok: false,
        message:
          'The current secure address setting is still loading. Please wait and try again.',
      });
      return;
    }
    if (!linkedCard) {
      setSecureResult({
        ok: false,
        message:
          'No card is linked to this account, so the secure address cannot be updated here. Use Card Maintenance.',
      });
      return;
    }
    setSecureUpdating(true);
    setSecureResult(null);
    try {
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/cards/secure-address', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          cardToken: linkedCard.CardToken,
          secureAddress: flag,
        },
      });
      if (apiError || !data) {
        // An RBAC action denial (e.g. this write is online-only for restricted
        // roles — #248) surfaces the authoritative verbatim copy inline; any
        // other 403 keeps its own distinct message.
        setSecureResult({
          ok: false,
          message:
            resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `Could not update the secure address (${response.status}).`),
        });
        return;
      }
      const result = data as unknown as {
        Updated?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
        SecureAddress?: string;
      };
      // Fail closed: only an EXPLICIT positive confirmation (`Updated: true`,
      // the field the BFF's `updateSecureAddress` adapter sets and audits on)
      // is treated as success. Any other 200 body — `{}`, `Updated:false`, or a
      // payload where `Updated` is missing due to mapper/schema drift — is a
      // FAILURE for this state-changing banking action, surfaced inline with the
      // gateway Code/Message for retry (#80/#82/#94, #468 ai-review). Never show
      // "Secure address updated" without that confirmation.
      if (result.Updated !== true) {
        const code = result.ResultCode ? `${result.ResultCode} — ` : '';
        setSecureResult({
          ok: false,
          message: `${code}${result.ResultMessage ?? 'Could not update the secure address. Please retry.'}`,
        });
        return;
      }
      // Persisted — reflect the gateway's confirmed flag and refresh the cached
      // card so the field stays truthful if the agent revisits it.
      const persisted =
        result.SecureAddress === 'Y' || result.SecureAddress === 'N'
          ? result.SecureAddress
          : flag;
      // The gateway-confirmed flag is the new persisted baseline: clear dirty so
      // it (and the refreshed cached card below) can re-seed the field cleanly
      // on a later panel revisit, and record it as the baseline a later reset
      // restores to (#468).
      secureAddressDirty.current = false;
      securePersistedFlag.current = persisted;
      // The commit was against this card's token — keep the enable guard's token
      // in sync so Update stays enabled for a follow-up change (#468 HIGH).
      securePersistedToken.current = linkedCard.CardToken;
      setSecureAddress(persisted);
      // The gateway-confirmed flag is now known — keep Update enabled for a
      // follow-up change.
      setSecureFlagStatus('loaded');
      setCards((prev) =>
        prev.map((c) =>
          c.CardToken === linkedCard.CardToken
            ? { ...c, SecureAddress: persisted }
            : c,
        ),
      );
      setSecureResult({ ok: true, message: 'Secure address updated.' });
    } catch {
      setSecureResult({ ok: false, message: 'Request failed. Please retry.' });
    } finally {
      setSecureUpdating(false);
    }
  }

  function resetStop(): void {
    setStopType('single');
    setChequeNumber('');
    setChequeFrom('');
    setChequeTo('');
    setChequeAmount('');
    setReason('');
    setCharge('');
    setStopScripting(false);
    setStopping(false);
    setStopError('');
    setStopSuccess('');
    setStopFieldErrors({});
  }

  function onAccountChange(value: string): void {
    accountRef.current = value;
    setAccount(value);
    setOption('');
    resetOrder();
    resetStop();
    // #869 — never carry the previous account's selections across.
    setOrderFields(EMPTY_ORDER_FIELDS);
  }

  function onOptionChange(value: string): void {
    setOption(value as ChequebookOption);
    resetOrder();
    resetStop();
  }

  // Place the chequebook order (CCF-36). Surfaces the gateway Code + Message
  // inline beside the button on failure, and a clear confirmation on success
  // (#80/#82/#94).
  async function onOrderChequebook(): Promise<void> {
    if (!hasContext || !account) return;
    setOrdering(true);
    setOrderError('');
    setOrderSuccess('');
    try {
      // #799 — defence in depth: the Order button is already suppressed on a
      // multi-signatory account (via `orderEligible`), but re-assert the block
      // here so the gateway can never be called for a "two to sign" account even
      // if the button were reached. The server enforces the same block (#173).
      if (requiresMultipleSignatories(selectedAccountRow)) {
        setOrderError(MULTIPLE_SIGNATORIES_MESSAGE);
        return;
      }
      // #869 — every order field must be chosen/valid; the button is already
      // disabled until then, and the BFF re-validates (400) server-side.
      const stillMissing = missingOrderFields(orderFields);
      if (stillMissing.length > 0) {
        setOrderError(
          `Complete the chequebook details before ordering: ${stillMissing.join(', ')}.`,
        );
        return;
      }
      // #538 — order-eligibility gate. The `chequebook/enquiry` can report the
      // order is NOT eligible (e.g. `OutstandingChequesExceeded:'Y'` — "the
      // number of outstanding cheques has been exceeded"). This is an APP
      // decision: like the 3-hold block (#185), the gateway is never called —
      // we re-read the enquiry at the order step so we act on a fresh snapshot
      // (not a stale render), re-derive eligibility, and if ineligible surface
      // the reason(s) inline beside the button and return early. `null` here
      // means the re-read failed / raced; fail closed and advise a retry.
      const fresh = await fetchEnquiry(account, false);
      const blockingReasons = deriveIneligibilityReasons(
        fresh,
        returnsBlockMessage,
      );
      if (!fresh || blockingReasons.length > 0) {
        setOrderError(
          blockingReasons.length > 0
            ? `This account is not eligible for a chequebook order: ${blockingReasons.join(
                ' ',
              )}`
            : 'Unable to confirm chequebook eligibility — please try again.',
        );
        return;
      }
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/chequebook-order', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: account,
          // #869 (AGW-84) — the confirmed / amended chequebook profile: Y/N
          // strings and integer code / books per order.
          postToCustomer: orderFields.postToCustomer as 'Y' | 'N',
          automaticOrdering: orderFields.automaticOrdering as 'Y' | 'N',
          irishChequeBookInd: orderFields.irishChequeBookInd as 'Y' | 'N',
          chequeBookCode: Number(orderFields.chequeBookCode) as ChequeBookCode,
          booksPerOrder: Number(orderFields.booksPerOrder),
        },
      });
      if (apiError || !data) {
        // #869 — the BFF refuses an invalid order field with a 400 + a stable
        // code; show the matching guidance inline beside the button.
        const fieldError =
          response.status === 400
            ? ORDER_FIELD_ERROR_MESSAGES[
                String((apiError as { error?: unknown } | undefined)?.error)
              ]
            : undefined;
        if (fieldError) {
          setOrderError(fieldError);
          return;
        }
        // Issue #248 — an RBAC *action* denial (`requirePermission` refuses the
        // blocked write with 403 `{ error: 'forbidden' }`) surfaces @dylan219's
        // authoritative verbatim copy inline beside the button — never a raw 403
        // or a generic error. Any OTHER 403 (e.g. the Teller-ID gate's
        // `{ error: 'invalid_teller_id' }`) keeps its own distinct message.
        setOrderError(
          resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `Could not place the chequebook order (${response.status}).`),
        );
        return;
      }
      const result = data as unknown as {
        Success?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
      };
      if (result.Success) {
        setOrderSuccess('Chequebook ordered successfully.');
        // Issue #337 — re-run the enquiry so the "Account details" tile picks
        // up whatever the gateway now reports for `LastNumberOrdered` /
        // `LastOrderDate` / `ChequeBookOrdered` / `OrderWithinThreshold`
        // instead of continuing to show the pre-order snapshot until the
        // agent navigates away and back. Best-effort: a failed refresh here
        // does not undo the order confirmation already shown above, nor does
        // it blank the already-rendered tile with a spurious error.
        void fetchEnquiry(account, false);
      } else if (result.ResultCode === 'THREE_HOLD') {
        // Issue #185 — the 3-hold BLOCK is an APP decision (the gateway is never
        // called); show the mandated advisory verbatim, no code prefix.
        setOrderError(
          result.ResultMessage ??
            "A 3 hold is currently applied to this account. Please confirm the customer's address is up to date and remove the hold where appropriate.",
        );
      } else if (result.ResultCode === 'HOLD_CHECK_FAILED') {
        // Issue #509 — the pre-post holds read failed (banking service down): the
        // order is blocked, but this is NOT a code-3 hold. Show the honest advisory
        // verbatim (no code prefix) so the agent can retry.
        setOrderError(
          result.ResultMessage ??
            'Unable to verify the account holds — the banking service returned an error. The order was not placed; please try again.',
        );
      } else if (result.ResultCode === 'SESSION_ALREADY_ORDERED') {
        // Issue #400 (CCF-36) — same-call/session guard: a chequebook was
        // already ordered for this account during THIS call/session. This is an
        // APP decision (the gateway is never called, per @dylan219's ruling), so
        // show the server's advisory verbatim beside the button (#80/#82), no
        // code prefix. A new call/session resets it and trusts the bank system.
        setOrderError(
          result.ResultMessage ??
            'A chequebook has already been ordered for this account during this call.',
        );
      } else {
        const friendly = result.ResultCode
          ? ORDER_DECLINE_MESSAGES[result.ResultCode]
          : undefined;
        setOrderError(
          friendly
            ? // Known decline: friendly reason, but keep the code (#80/#82/#94).
              `${friendly} (${result.ResultCode})`
            : `Chequebook order failed${
                result.ResultCode ? ` (${result.ResultCode})` : ''
              }${result.ResultMessage ? `: ${result.ResultMessage}` : '.'}`,
        );
      }
    } catch {
      setOrderError('Request failed. Please try again.');
    } finally {
      setOrdering(false);
    }
  }

  // Stop a cheque / range (CCF-37/78). Calls `/accounts/cheque-stop`; surfaces
  // the gateway Code + Message inline on a decline (e.g. cheque already presented
  // through clearing) and a clear confirmation on success (#80/#82/#94).
  async function onStopCheque(): Promise<void> {
    if (!hasContext || !account) return;
    // #799 increment 3 — defence in depth: the Stop cheque submit is already
    // disabled on a multi-signatory account, but re-assert the block here so the
    // gateway can never be called for a "two to sign" account even if the button
    // were reached. The server enforces the same block (#173).
    if (requiresMultipleSignatories(selectedAccountRow)) {
      setStopError(MULTIPLE_SIGNATORIES_MESSAGE);
      return;
    }
    // Mandatory-field validation (#508). The single "Cheque number" (single mode)
    // and the "From"/"To" range fields (range mode) are required — surface an
    // inline warning beside each empty field and BLOCK the submit rather than
    // sending the gateway a request with a missing value. Recoverable: the agent
    // fills the field and re-submits.
    const fieldErrors: {
      chequeNumber?: string;
      chequeFrom?: string;
      chequeTo?: string;
      reason?: string;
    } = {};
    if (stopType === 'single') {
      if (!chequeNumber.trim()) {
        fieldErrors.chequeNumber = 'Cheque number is required.';
      }
    } else {
      if (!chequeFrom.trim()) {
        fieldErrors.chequeFrom = 'From (inclusive) is required.';
      }
      if (!chequeTo.trim()) {
        fieldErrors.chequeTo = 'To (inclusive) is required.';
      }
    }
    if (!reason) {
      fieldErrors.reason = 'Reason for stopping is required.';
    }
    if (Object.keys(fieldErrors).length > 0) {
      setStopFieldErrors(fieldErrors);
      return;
    }
    setStopFieldErrors({});
    setStopping(true);
    setStopError('');
    setStopSuccess('');
    try {
      const reasonLabel =
        STOP_REASONS.find((r) => r.value === reason)?.label ?? reason;
      const {
        data,
        error: apiError,
        response,
      } = await api.POST('/accounts/cheque-stop', {
        params: { header: { 'X-Teller-Id': tellerId } },
        body: {
          customerNumber,
          accountNumber: account,
          reason: reasonLabel,
          charge: charge === 'Yes' ? 'Y' : 'N',
          ...(stopType === 'single'
            ? {
                chequeNumber: chequeNumber.trim(),
                ...(chequeAmount.trim()
                  ? { chequeAmount: chequeAmount.trim() }
                  : {}),
              }
            : {
                chequeFrom: chequeFrom.trim(),
                chequeTo: chequeTo.trim(),
              }),
        },
      });
      if (apiError || !data) {
        // An RBAC action denial surfaces the authoritative verbatim copy inline
        // (#248); any other 403 keeps its own message.
        setStopError(
          resolveActionBlockMessage(response.status, apiError) ??
            (response.status === 403
              ? 'Forbidden: check your Teller ID and permissions.'
              : `Could not stop the cheque (${response.status}).`),
        );
        return;
      }
      const result = data as unknown as {
        Success?: boolean;
        ResultCode?: string;
        ResultMessage?: string;
      };
      if (result.Success) {
        setStopSuccess(
          stopType === 'range'
            ? 'Cheque range stopped successfully.'
            : 'Cheque stopped successfully.',
        );
      } else if (result.ResultCode === 'THREE_HOLD') {
        // The 3-hold BLOCK is an APP decision (the gateway is never called);
        // show the mandated advisory verbatim, no code prefix (#185).
        setStopError(
          result.ResultMessage ??
            "A 3 hold is currently applied to this account. Please confirm the customer's address is up to date and remove the hold where appropriate.",
        );
      } else if (result.ResultCode === 'MULTIPLE_SIGNATORIES') {
        // #799 increment 3 — the multi-signatory BLOCK is likewise an APP
        // decision (the gateway stop op is never called); show the mandated
        // advisory verbatim, no code prefix (mirrors the THREE_HOLD handling).
        setStopError(result.ResultMessage ?? MULTIPLE_SIGNATORIES_MESSAGE);
      } else if (result.ResultCode === 'HOLD_CHECK_FAILED') {
        // Issue #509 — pre-post holds read failed (banking service down): blocked,
        // but NOT a code-3 hold. Honest advisory verbatim, no code prefix; retryable.
        setStopError(
          result.ResultMessage ??
            'Unable to verify the account holds — the banking service returned an error. The order was not placed; please try again.',
        );
      } else {
        const friendly = result.ResultCode
          ? STOP_DECLINE_MESSAGES[result.ResultCode]
          : undefined;
        setStopError(
          friendly
            ? `${friendly} (${result.ResultCode})`
            : `Cheque stop failed${
                result.ResultCode ? ` (${result.ResultCode})` : ''
              }${result.ResultMessage ? `: ${result.ResultMessage}` : '.'}`,
        );
      }
    } catch {
      setStopError('Request failed. Please try again.');
    } finally {
      setStopping(false);
    }
  }

  // #471 (@dylan219) — only offer accounts the gateway permits a chequebook on:
  // filter the selector to `customer/accounts/list` rows where
  // `ChequeBookPermittedInd === 'Y'`. An account whose flag is absent or not 'Y'
  // is excluded (a chequebook cannot be ordered/serviced on it), so the agent is
  // never presented an ineligible account.
  const accountOptions = accounts
    .filter((a) => a.ChequeBookPermittedInd === 'Y')
    .map((a) => ({
      value: a.AccountNumber,
      label: accountOptionLabel(a),
    }));

  // A chequebook was already ordered within the gateway's recent-order window
  // (#171): the enquiry flags it via `OrderWithinThreshold` ONLY (#353 — see the
  // already-ordered doc comment above for why `ChequeBookOrdered` must
  // NOT be included here). #743 11:28Z: `OrderWithinThreshold:'Y'` = ordered
  // WITHIN X DAYS → the inline advisory shows the clear-the-counter message.
  const alreadyOrdered = enquiry?.OrderWithinThreshold === 'Y';

  // #714 (@dylan219): `ChequeBookOrdered:'Y'` means a chequebook has already
  // been ordered on the account (ordered TODAY per @dylan219's #743 11:28Z
  // correction) and the agent must NOT be allowed to place a
  // further order. When the enquiry returns this flag the Order button is blocked
  // and the block message is shown inline (#743 table copy — see the JSX below).
  // This supersedes the #353
  // "lifetime flag / do not block" interpretation: Dylan's ruling in #714 is that
  // `ChequeBookOrdered` is the authoritative "already ordered" signal and the
  // agent should be shown this message.
  // #714 (fix): suppress the block immediately after a successful order in the
  // current session — the re-fetch after success returns `ChequeBookOrdered:'Y'`
  // which would otherwise flash the error over the success message. The block
  // reappears correctly if the agent re-opens the "Order chequebook" option
  // (which calls `resetOrder()`, clearing `orderSuccess`).
  const chequebookAlreadyOrdered =
    enquiry?.ChequeBookOrdered === 'Y' && !orderSuccess;

  // Order-chequebook eligibility (#497 — the "Order chequebook" figma shows
  // `{{ChequeOrderEligibilityStatus}}` + `{{ReasonsiFineligible}}`). Derive the
  // overall Yes/No from the `chequebook/enquiry` flags and, when No, the list of
  // reasons WHY it is not eligible so the agent can explain it to the customer.
  // Shared with the order-step re-check (#538) via `deriveIneligibilityReasons`
  // so the badge and the block decision can never diverge. Eligible ONLY when
  // every signal is clear.
  const ineligibilityReasons: string[] = deriveIneligibilityReasons(
    enquiry,
    returnsBlockMessage,
  );
  // #714: the order is also ineligible when `ChequeBookOrdered:'Y'`.
  // #799: and when the account requires more than one signatory — a blocked
  // action on a "two to sign" account. Folding it into `orderEligible` suppresses
  // the fee scripting + Order button (the #557 gate) and shows only the block
  // message, exactly like the other ineligibility cases.
  const orderEligible =
    !!enquiry &&
    ineligibilityReasons.length === 0 &&
    !chequebookAlreadyOrdered &&
    !selectedAccountMultiSignatory;

  // #869 (CCRS-3795/3796) — order-field derived display values.
  const orderFieldsMissing = missingOrderFields(orderFields);
  const selectedCode = CHEQUE_BOOK_CODES.find(
    (c) => String(c.code) === orderFields.chequeBookCode,
  );
  const orderTypeLabel =
    enquiry?.FirstTimeOrder === 'Y'
      ? 'First-time order'
      : enquiry?.FirstTimeOrder === 'N'
        ? 'Re-order'
        : '—';
  const branchLines = [
    enquiry?.BranchAddress1,
    enquiry?.BranchAddress2,
    enquiry?.BranchAddress3,
  ]
    .map((line) => (typeof line === 'string' ? line.trim() : ''))
    .filter((line) => line !== '');

  return (
    <main
      className="max-w-[1800px] mx-auto px-12 py-12"
      aria-labelledby="chequebook-heading"
    >
      <div className="flex items-center justify-between mb-8 gap-8 flex-wrap">
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate('/dashboard');
          }}
          className="inline-flex items-center gap-2 text-[16px] font-['Arial',sans-serif] text-[#FF703E] underline hover:text-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:ring-offset-2 rounded"
        >
          &lt; Back to customer dashboard
        </a>

        {/* Clear cheque counter — only when an option is selected (Figma).
            Clearing the counter is actioned by the Current Accounts Department,
            not on the call, so this pops the "Clear Cheque Counter" email
            template (Drive "SCRN 41", #254) as a prefilled draft addressed to
            Current Accounts, best-effort prefilled with the in-context customer
            name + selected account number. Delivered as a formatted downloadable
            Outlook draft (`.eml`, #434) since the template provides `buildHtml`;
            falls back to the plain-text `mailto:` if it ever doesn't. */}
        {option && (
          <button
            type="button"
            onClick={() => {
              const ctx = {
                customerName: ownerName,
                accountNumber: account,
              };
              if (CLEAR_CHEQUE_COUNTER.buildHtml) {
                downloadTemplateEml(
                  CLEAR_CHEQUE_COUNTER,
                  ctx,
                  undefined,
                  tellerId,
                );
              } else {
                openTemplateEmail(CLEAR_CHEQUE_COUNTER, ctx);
              }
            }}
            aria-label="Clear Cheque Counter"
            className="inline-flex items-center justify-center px-6 py-3 min-h-[44px] bg-[#FF703E] text-[#1D252C] font-['Arial',sans-serif] text-[16px] font-bold rounded-[4px] shadow-sm hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-all"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m2 7 10 6 10-6" />
            </svg>
            <span className="ml-2">Clear Cheque Counter</span>
          </button>
        )}
      </div>

      <div className="flex items-center justify-between gap-8 mb-8 flex-wrap">
        <h1
          id="chequebook-heading"
          className="text-[28px] font-['Arial',sans-serif] font-bold text-[#1d252c] m-0"
        >
          Chequebook
        </h1>
        {/* Process-menu navigation (issue #45): jump to a related process.
            Pure navigation chooser — each route stays independently RBAC-gated
            (deny-by-default). */}
        <ProcessMenu
          options={PROCESS_MENU_OPTIONS}
          onSelect={onProcessSelect}
        />
      </div>

      {accountsError && (
        <div
          role="alert"
          className="mb-6 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[900px]"
        >
          {accountsError}
        </div>
      )}

      {/* Account selection (CCF-36). */}
      <div className="mb-6 w-[400px]">
        <Dropdown
          id="chequebook-account"
          label="Select account"
          placeholder="Select account"
          value={account}
          onChange={onAccountChange}
          options={accountOptions}
        />
      </div>

      {/* Account details (CCF-36): order eligibility / fees / outstanding-cheque
          counts from the chequebook enquiry. */}
      {account && (
        <div className="max-w-[900px] mb-6 border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Account details
          </h2>
          {enquiryLoading ? (
            <p className="text-[16px] font-['Arial',sans-serif] text-[#6b7280] m-0">
              Loading…
            </p>
          ) : enquiryError ? (
            <div
              role="alert"
              className="p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c]"
            >
              {enquiryError}
            </div>
          ) : enquiry ? (
            // #497 — the account-details tile matches the customer figma: IBAN,
            // Available Balance, Customer type, Owner(s) on the left; Outstanding
            // Cheques, Last cheque book ordered, Last Date Ordered and Holds on the
            // right. The IBAN / balance / owner / holds come from the selected
            // `customer/accounts/list` row, the customer type from the dashboard
            // `Segment`, and the cheque counts/last-order fields from the
            // `chequebook/enquiry`. The eligibility flags that previously sat here
            // (Order eligible / Account hold / Sufficient funds / Recently ordered)
            // now drive the Order-chequebook eligibility display below.
            <div className="grid grid-cols-2 gap-x-16 gap-y-6">
              <DetailField
                label="IBAN"
                value={selectedAccountRow?.Iban || '—'}
              />
              <DetailField
                label="Outstanding Cheques:"
                value={
                  enquiry.NumberChequesOutstanding != null
                    ? String(enquiry.NumberChequesOutstanding)
                    : '—'
                }
              />
              <DetailField
                label="Available Balance:"
                value={
                  // #415 — render with the account's OWN currency symbol, never
                  // a hardcoded euro sign (a foreign-currency account shows its
                  // real symbol).
                  selectedAccountRow?.AvailableBalance != null
                    ? formatCurrency(
                        selectedAccountRow.AvailableBalance,
                        selectedAccountRow.Currency || 'EUR',
                      )
                    : '—'
                }
              />
              <DetailField
                label="Last cheque book ordered:"
                value={enquiry.LastNumberOrdered || '—'}
              />
              <DetailField
                label="Customer type:"
                value={customerType || customer.segment || '—'}
              />
              <DetailField
                label="Last Date Ordered:"
                value={enquiry.LastOrderDate || '—'}
              />
              {/* Owner(s): ALL holders of a joint account (#414), not just the
                  in-context caller. */}
              <DetailField label="Owner(s):" value={accountOwners || '—'} />
              <DetailField
                label="Holds:"
                value={
                  selectedAccountRow?.Holds &&
                  selectedAccountRow.Holds.length > 0
                    ? selectedAccountRow.Holds.map((h) =>
                        String(h?.Code ?? '').trim(),
                      )
                        .filter((c) => c !== '')
                        .join(',') || '—'
                    : '—'
                }
              />
            </div>
          ) : null}
        </div>
      )}

      {/* Chequebook options dropdown (CCF-36) — Order / Stopping cheques. */}
      {account && enquiry && (
        <div className="mb-6 w-[400px]">
          <Dropdown
            id="chequebook-options"
            label="Chequebook options"
            placeholder="Select option"
            value={option}
            onChange={onOptionChange}
            options={CHEQUEBOOK_OPTIONS}
          />
        </div>
      )}

      {/* Order chequebook (CCF-36 / CCF-76). */}
      {option === 'order' && enquiry && (
        <div className="max-w-[1200px] border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Order chequebook
          </h2>

          {/* #497 — Order eligibility status + reasons, mirroring the figma's
              `{{ChequeOrderEligibilityStatus}}` / `{{ReasonsiFineligible}}`
              placeholders. Shows "Order Eligible: Yes/No" and, when No, a
              "Reason for ineligibility:" list of the specific gateway signals
              (account type / hold / funds / outstanding cheques / recent order).
              Derived from the live `chequebook/enquiry` flags. */}
          <div className="mb-8">
            <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
              <span className="font-bold">Order Eligible:</span>{' '}
              {orderEligible ? 'Yes' : 'No'}
            </p>
            {!orderEligible && ineligibilityReasons.length > 0 && (
              <div className="mt-2">
                <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0 font-bold">
                  Reason for ineligibility:
                </p>
                <ul className="list-disc pl-6 mt-1 text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                  {ineligibilityReasons.map((reasonText) => (
                    <li key={reasonText}>{reasonText}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Block a repeat order within the recent-order window (#171): when
              the enquiry reports a chequebook was ordered within the gateway's
              threshold, show the advisory. #743 (@dylan219 11:28Z correction):
              `OrderWithinThreshold:'Y'` = ordered WITHIN X DAYS → the mandated
              response is the clear-the-counter message. This is an
              ineligibility case (#557 hides the fee scripting / order controls
              below), but the specific #171 guidance is retained here — beside
              the reason list — rather than lost. */}
          {alreadyOrdered && (
            <div
              role="alert"
              className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px] whitespace-pre-line"
            >
              The chequebook cannot be ordered at this time. Please clear the
              chequebook counter using the standard email template.
            </div>
          )}

          {/* #714 (@dylan219): when `ChequeBookOrdered:'Y'` is returned by the
              enquiry, the agent must be blocked from ordering and shown the
              block message. The scripting + order controls are suppressed via
              `orderEligible` (which now accounts for this flag).
              #743 (@dylan219 field→response table, 2026-09-02, 11:28Z
              correction): `ChequeBookOrdered:'Y'` = chequebook ordered TODAY,
              so this banner shows the ordered-today copy — the admin-editable
              content-catalogue text (`chequebook.order.already_ordered`, #637)
              whose compiled-in default is "Chequebook already ordered today.
              Please try again tomorrow." (the block LOGIC and the #714
              orderSuccess suppression are unchanged).
              Suppressed while `orderSuccess` is set (immediately after a
              successful order in this session) — the re-fetch returns
              `ChequeBookOrdered:'Y'` but the success banner is already shown;
              the block reappears if the agent re-opens the order option. */}
          {chequebookAlreadyOrdered && (
            <div
              role="alert"
              className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
            >
              {alreadyOrderedMessage}
            </div>
          )}

          {/* #799 (@dylan219): the selected account requires more than one
              signatory ("two to sign" mandate), so Chequebook Order is NOT
              permitted through the desktop. Show the verbatim block message
              inline beside the action — the fee scripting + Order button are
              suppressed via `orderEligible` (which accounts for this flag). The
              agent can select a different account. Enforced server-side too so a
              direct API call cannot bypass it (#173). */}
          {selectedAccountMultiSignatory && (
            <div
              role="alert"
              className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
            >
              {MULTIPLE_SIGNATORIES_MESSAGE}
            </div>
          )}

          {/* #869 (CCRS-3795 / CCRS-3796, gateway change AGW-84) — the
              chequebook details sent with the order. Pre-filled from the
              `chequebook/enquiry` profile and amendable by the agent; shown
              (and editable) whatever the eligibility status. A value on file
              that is not a valid choice (a code outside the catalogue such as
              0, or an empty Post to customer) is left UNSELECTED — never
              invented — and the Order button stays disabled until the agent
              chooses. First-time vs re-order is decided by Unibanks and is
              read-only. */}
          <section
            aria-labelledby="chequebook-details-heading"
            className="mb-8 border border-[#dad7d3] p-6"
          >
            <h3
              id="chequebook-details-heading"
              className="text-[18px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-4 mt-0"
            >
              Chequebook details
            </h3>
            <div className="grid grid-cols-2 gap-x-8 gap-y-4 max-w-[800px]">
              <DetailField label="Order type" value={orderTypeLabel} />
              <div />

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="cb-post-to-customer"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Post to customer
                </label>
                <select
                  id="cb-post-to-customer"
                  value={orderFields.postToCustomer}
                  onChange={(e) =>
                    setOrderFields((f) => ({
                      ...f,
                      postToCustomer: toYesNo(e.target.value),
                    }))
                  }
                  className="px-2 py-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db] rounded focus:outline-none focus:ring-2 focus:ring-[#005fcc]"
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>
              <div>
                {orderFields.postToCustomer === 'N' && (
                  <div
                    data-testid="chequebook-branch"
                    className="text-[14px] font-['Arial',sans-serif] text-[#1d252c]"
                  >
                    <p className="m-0 font-bold">Branch</p>
                    <p className="m-0">{enquiry.BranchName?.trim() || '—'}</p>
                    {branchLines.map((line) => (
                      <p key={line} className="m-0">
                        {line}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="cb-code"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Chequebook code
                </label>
                <select
                  id="cb-code"
                  value={orderFields.chequeBookCode}
                  onChange={(e) =>
                    setOrderFields((f) => ({
                      ...f,
                      chequeBookCode: e.target.value,
                    }))
                  }
                  className="px-2 py-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db] rounded focus:outline-none focus:ring-2 focus:ring-[#005fcc]"
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  {CHEQUE_BOOK_CODES.map((c) => (
                    <option key={c.code} value={String(c.code)}>
                      {c.code} – {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <DetailField
                label="Cheques per book"
                value={selectedCode ? String(selectedCode.chequesPerBook) : '—'}
              />

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="cb-automatic-ordering"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Automatic ordering
                </label>
                <select
                  id="cb-automatic-ordering"
                  value={orderFields.automaticOrdering}
                  onChange={(e) =>
                    setOrderFields((f) => ({
                      ...f,
                      automaticOrdering: toYesNo(e.target.value),
                    }))
                  }
                  className="px-2 py-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db] rounded focus:outline-none focus:ring-2 focus:ring-[#005fcc]"
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="cb-irish"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Irish chequebook
                </label>
                <select
                  id="cb-irish"
                  value={orderFields.irishChequeBookInd}
                  onChange={(e) =>
                    setOrderFields((f) => ({
                      ...f,
                      irishChequeBookInd: toYesNo(e.target.value),
                    }))
                  }
                  className="px-2 py-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db] rounded focus:outline-none focus:ring-2 focus:ring-[#005fcc]"
                >
                  <option value="" disabled>
                    Select…
                  </option>
                  <option value="Y">Y</option>
                  <option value="N">N</option>
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="cb-books-per-order"
                  className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c]"
                >
                  Books per order
                </label>
                {/* Numeric-only (digits stripped on input), 1–99. */}
                <input
                  id="cb-books-per-order"
                  type="text"
                  inputMode="numeric"
                  maxLength={2}
                  value={orderFields.booksPerOrder}
                  onChange={(e) =>
                    setOrderFields((f) => ({
                      ...f,
                      booksPerOrder: e.target.value
                        .replace(/\D/g, '')
                        .slice(0, 2),
                    }))
                  }
                  className="w-[80px] px-2 py-1 text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db] rounded focus:outline-none focus:ring-2 focus:ring-[#005fcc]"
                />
              </div>
            </div>
            {orderFieldsMissing.length > 0 && (
              <p
                data-testid="chequebook-details-hint"
                className="mt-4 mb-0 text-[14px] font-['Arial',sans-serif] text-[#1d252c] p-2 border border-[#d97706] max-w-[600px]"
              >
                Choose or enter before ordering: {orderFieldsMissing.join(', ')}
                .
              </p>
            )}
          </section>

          {/* #557 — everything below the "Reason for ineligibility" list (the
              CIF/Postal address panel, the fee scripting and the Scripting-
              complete tick) is order-servicing UI that only applies when the
              chequebook order is actually eligible. When the account is NOT
              eligible ("Order Eligible: No"), the agent can neither place the
              order nor service the address for it, so we render ONLY the
              eligibility status + reason(s) and hide the rest — the customer
              should not be walked through fees/scripting for an order that can't
              proceed. The eligible path is unchanged. */}
          {orderEligible && (
            <>
              {/* Issue #169 — CIF + Postal address sourced from the gateway (the
              shared panel Card Maintenance / Statements use), NOT placeholder
              text. The secure-address field + Issuing Branch are rendered in the
              panel's action column. "Change address page" routes to the Change
              Customer Details screen (SCRN-24, `/personal-details`) — per #256
              (dylan219), reversing the earlier #52/!193 wiring to the
              postal-address screen. */}
              <div className="mb-8">
                <CustomerAddressPanel
                  cifAddress={cifAddress}
                  cifNumber={customerNumber}
                  eircode={cifEircode}
                  postalAddress={postalAddress}
                  changeAddressPath="/personal-details"
                  actions={
                    <>
                      <p className="text-[12px] font-['Arial',sans-serif] text-[#1d252c] m-0 max-w-[300px]">
                        Check if eircode is populated in the eircode field that
                        it is also in Line 5 of the CIF address.
                      </p>

                      <hr className="border-t border-[#dad7d3] my-4" />

                      <div className="flex items-center gap-2">
                        <label
                          htmlFor="secure-address"
                          className="text-[14px] font-['Arial',sans-serif] font-bold text-[#1d252c] whitespace-nowrap"
                        >
                          Secure address:
                        </label>
                        {/* Constrained to the only two valid gateway values (Y/N) —
                        a free-text field let a typo like `X` be coerced to `Y`
                        (a real state change); a select makes that impossible
                        (#468 ai-review). A leading blank option represents the
                        UNKNOWN state while the authoritative persisted flag loads
                        — the field never defaults to 'Y' (#468 ai-review HIGH). */}
                        <select
                          id="secure-address"
                          value={secureAddress}
                          onChange={(e) => {
                            // Mark the field touched so a late-arriving authoritative
                            // seed can no longer overwrite the agent's choice
                            // (#468 ai-review MEDIUM).
                            secureAddressDirty.current = true;
                            setSecureAddress(e.target.value);
                            // A fresh edit invalidates the last commit outcome so a
                            // stale "updated" line can never sit beside a changed
                            // value (#468/#80/#82/#94).
                            setSecureResult(null);
                          }}
                          disabled={!secureFlagReady}
                          aria-label="Secure address"
                          className="w-[60px] px-2 py-1 text-center text-[14px] font-['Arial',sans-serif] text-[#1d252c] border border-[#d1d5db] rounded focus:outline-none focus:ring-2 focus:ring-[#005fcc] disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          <option value="" disabled>
                            —
                          </option>
                          <option value="Y">Y</option>
                          <option value="N">N</option>
                        </select>
                        {/* Commit the flag against the account's card via the R4
                        `card/secure-address` op (#468, CCF-77). Success is shown
                        ONLY when the gateway confirms it; an error is surfaced
                        inline for retry (CCF-77 Unhappy Path #1) — no more
                        optimistic/false success. Disabled until the AUTHORITATIVE
                        persisted flag has loaded (`secureFlagStatus === 'loaded'`)
                        so an agent can never submit a defaulted/unknown value over
                        a real persisted flag (#468 ai-review HIGH). */}
                        <button
                          type="button"
                          onClick={() => void onUpdateSecureAddress()}
                          disabled={
                            secureUpdating ||
                            !secureFlagReady ||
                            (secureAddress !== 'Y' && secureAddress !== 'N')
                          }
                          className="bg-[#FF703E] text-[#1d252c] px-4 py-1 rounded text-[14px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-2 focus:ring-[#005fcc] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {secureUpdating ? 'Updating…' : 'Update'}
                        </button>
                      </div>

                      {/* Loading / unknown state for the authoritative persisted flag
                      (#468). While it loads — or if the authoritative read failed
                      — the field is unknown and Update is disabled, so a defaulted
                      value can never be committed over a real persisted flag. */}
                      {(secureFlagStatus === 'loading' ||
                        secureFlagStatus === 'idle') && (
                        <p className="text-[12px] font-['Arial',sans-serif] text-[#6b7280] m-0 max-w-[300px]">
                          Loading the current secure address setting…
                        </p>
                      )}
                      {secureFlagStatus === 'no-card' && (
                        <p
                          role="alert"
                          className="text-[12px] font-['Arial',sans-serif] text-[#1d252c] p-2 border border-[#dc2626] max-w-[300px]"
                        >
                          No card is linked to this account, so the secure
                          address cannot be updated here. Use Card Maintenance.
                        </p>
                      )}
                      {/* Fail-closed when more than one card is linked to the account
                      (#468 ai-review HIGH): a state-changing write must target an
                      unambiguous card, so direct the agent to Card Maintenance
                      rather than silently updating an arbitrary one. */}
                      {secureFlagStatus === 'ambiguous' && (
                        <p
                          role="alert"
                          className="text-[12px] font-['Arial',sans-serif] text-[#1d252c] p-2 border border-[#dc2626] max-w-[300px]"
                        >
                          More than one card is linked to this account, so the
                          secure address cannot be updated here without
                          ambiguity. Use Card Maintenance to update the specific
                          card.
                        </p>
                      )}
                      {secureFlagStatus === 'error' && (
                        <div
                          role="alert"
                          className="flex items-center gap-2 text-[12px] font-['Arial',sans-serif] text-[#1d252c] p-2 border border-[#dc2626] max-w-[300px]"
                        >
                          <span>
                            Could not load the current secure address setting.
                          </span>
                          {/* Explicit retry (#468 ai-review MEDIUM): the same
                          `secureFlagStatus === 'error'` UI covers TWO distinct
                          failures — the per-card `/cards/postal-address` flag read
                          failing, AND the upstream `/cards` read failing (no card
                          resolves). Retry must recover BOTH: always bump the flag
                          reload nonce (the seeding effect is keyed on the card
                          token so reselecting the same account would NOT re-fire
                          it), and when the CARDS read itself errored, also bump the
                          cards reload nonce so `/cards` is re-fetched — otherwise
                          the seeding effect just returns to 'error' with an empty
                          card list and the agent is stuck behind a dead Retry. */}
                          <button
                            type="button"
                            onClick={() => {
                              if (cardsStatus === 'error')
                                setCardsReload((n) => n + 1);
                              setSecureFlagReload((n) => n + 1);
                            }}
                            className="underline text-[#005fcc] hover:no-underline focus:outline-none focus:ring-2 focus:ring-[#005fcc]"
                          >
                            Retry
                          </button>
                        </div>
                      )}

                      {/* Inline outcome beside the Update button (#80/#82/#94). */}
                      {secureResult && (
                        <div
                          role={secureResult.ok ? 'status' : 'alert'}
                          className={`text-[12px] font-['Arial',sans-serif] text-[#1d252c] p-2 border max-w-[300px] ${
                            secureResult.ok
                              ? 'border-[#16a34a]'
                              : 'border-[#dc2626]'
                          }`}
                        >
                          {secureResult.message}
                        </div>
                      )}

                      {secureAddress === 'N' && (
                        <div className="text-[12px] font-['Arial',sans-serif] text-[#1d252c]">
                          {/* Issuing branch code = first 6 digits of the account
                          number after the PTSB prefix, derived client-side
                          (#188, @dylan219 — the SAME method used on the Card
                          Maintenance journey). */}
                          <span className="font-bold">Issuing Branch:</span>{' '}
                          {deriveIssuingBranchCode(account) ?? '—'}
                        </div>
                      )}
                    </>
                  }
                />
              </div>

              {/* Charge scripting + order (CCF-36). */}
              <div className="border-t-2 border-[#dad7d3] pt-6">
                <div className="flex gap-4 mb-6">
                  <div
                    className="border-l-4 border-[#FF703E]"
                    aria-hidden="true"
                  />
                  <div>
                    {/* Charge/delivery scripting — verbatim per CCF-76 acceptance
                    criteria (issue #510): the AC lists the 40/50-cheque charges,
                    the per-cheque breakdown, the Jubilee-customer exception, and
                    the delivery timeframe. */}
                    <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] mb-4">
                      Charge is €28 to order a chequebook with 40 cheques.
                      <br />
                      Charge is €35 to order a chequebook with 50 cheques.
                      <br />
                      Breakdown: €0.70 per cheque (€0.50 Government Stamp Duty +
                      €0.20 PTSB charge).
                      <br />
                      Jubilee customers pay Government Stamp Duty only (€20.00
                      total — €0.50 per cheque).
                    </p>
                    <p className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] m-0">
                      Delivery Timeframe: 5–7 working days. Would you like me to
                      proceed?
                    </p>
                  </div>
                </div>

                {/* #557 — this whole scripting + order block only renders when the
                order is eligible (the enclosing `orderEligible` gate), so no
                ineligible/already-ordered branch is needed here: those cases are
                handled above beside the "Reason for ineligibility" list (the
                #171 already-ordered advisory) and never reach this point. */}
                <div className="mb-6 flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="order-scripting"
                    checked={orderScripting}
                    onChange={() => setOrderScripting((c) => !c)}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <label
                    htmlFor="order-scripting"
                    className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                  >
                    Scripting complete
                  </label>
                </div>

                {orderScripting && (
                  <button
                    type="button"
                    onClick={() => void onOrderChequebook()}
                    // #538 — defence in depth: the handler re-reads the enquiry and
                    // re-checks eligibility before calling the gateway even though
                    // this block only renders when `orderEligible`.
                    // #869 — and every chequebook detail must be chosen.
                    disabled={
                      ordering ||
                      !orderEligible ||
                      orderFieldsMissing.length > 0
                    }
                    className="bg-[#FF703E] text-[#1d252c] px-6 py-3 rounded-[4px] text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {ordering ? 'Ordering…' : 'Order chequebook'}
                  </button>
                )}
              </div>
            </>
          )}

          {/* Inline outcome beside the action (#80/#82/#94). Rendered OUTSIDE
              the `orderEligible` gate (#557): a SUCCESSFUL order triggers a
              post-order enquiry refresh (#337) that can report the account as
              now recently-ordered (`OrderWithinThreshold:'Y'`) — flipping it to
              ineligible and hiding the scripting/order block. The confirmation
              (and any error) must persist through that flip rather than vanish
              the instant the order succeeds. */}
          {orderError && (
            <div
              role="alert"
              className="mt-4 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
            >
              {orderError}
            </div>
          )}
          {orderSuccess && (
            <div
              role="status"
              className="mt-4 p-4 bg-white border border-[#16a34a] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
            >
              {orderSuccess}
            </div>
          )}
        </div>
      )}

      {/* Stopping cheques (CCF-37 / CCF-78) — wired to the Bank API (#147). */}
      {option === 'stop' && (
        <div className="max-w-[800px] border-2 border-[#dad7d3] bg-white p-8">
          <h2 className="text-[24px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-6">
            Stopping cheques
          </h2>

          {/* #799 increment 3 (@dylan219): the selected account requires more
              than one signatory ("two to sign" mandate), so Stopping Cheque(s)
              is NOT permitted through the desktop. The verbatim block message
              shows inline beside the action and the Stop cheque submit is
              disabled below. The agent can select a different account. Enforced
              server-side on /accounts/cheque-stop too, so a reload / direct API
              call cannot bypass it (#173). */}
          {selectedAccountMultiSignatory && (
            <div
              role="alert"
              className="mb-8 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
            >
              {MULTIPLE_SIGNATORIES_MESSAGE}
            </div>
          )}

          {/* Stop a cheque / Stop cheque range selector. */}
          <div className="flex gap-4 mb-8 flex-wrap">
            <button
              type="button"
              onClick={() => {
                setStopType('single');
                setStopError('');
                setStopSuccess('');
                setStopFieldErrors({});
              }}
              className={`px-6 py-3 rounded-md text-[16px] font-['Arial',sans-serif] font-bold transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] ${
                stopType === 'single'
                  ? 'bg-[#FF703E] text-[#1d252c]'
                  : 'bg-white text-[#FF703E] border-2 border-[#FF703E]'
              }`}
            >
              Stop a cheque
            </button>
            <button
              type="button"
              onClick={() => {
                setStopType('range');
                setStopError('');
                setStopSuccess('');
                setStopFieldErrors({});
              }}
              className={`px-6 py-3 rounded-md text-[16px] font-['Arial',sans-serif] font-bold transition-all focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] ${
                stopType === 'range'
                  ? 'bg-[#FF703E] text-[#1d252c]'
                  : 'bg-white text-[#FF703E] border-2 border-[#FF703E]'
              }`}
            >
              Stop cheque range
            </button>
          </div>

          <div className="max-w-[600px] space-y-6">
            {/* Single cheque number. */}
            {stopType === 'single' && (
              <div>
                <label
                  htmlFor="cheque-number"
                  className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] block mb-2"
                >
                  Cheque number
                </label>
                <input
                  id="cheque-number"
                  type="text"
                  value={chequeNumber}
                  onChange={(e) => {
                    setChequeNumber(e.target.value);
                    // Clear the mandatory-field warning once the agent types
                    // (#508) — recoverable inline error, never sticky.
                    if (stopFieldErrors.chequeNumber)
                      setStopFieldErrors((p) => ({
                        ...p,
                        chequeNumber: undefined,
                      }));
                  }}
                  placeholder="Enter cheque number"
                  aria-required="true"
                  aria-invalid={!!stopFieldErrors.chequeNumber}
                  aria-describedby={
                    stopFieldErrors.chequeNumber
                      ? 'cheque-number-error'
                      : undefined
                  }
                  className={`border rounded-md px-4 py-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] w-[350px] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-transparent ${
                    stopFieldErrors.chequeNumber
                      ? 'border-[#dc2626]'
                      : 'border-[#c4c4c4]'
                  }`}
                />
                {stopFieldErrors.chequeNumber && (
                  <p
                    id="cheque-number-error"
                    role="alert"
                    className="mt-2 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
                  >
                    {stopFieldErrors.chequeNumber}
                  </p>
                )}
              </div>
            )}

            {/* Cheque number range. */}
            {stopType === 'range' && (
              <div>
                <span className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] block mb-4">
                  Cheque number range
                </span>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label
                      htmlFor="cheque-from"
                      className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] block mb-2"
                    >
                      From (inclusive)
                    </label>
                    <input
                      id="cheque-from"
                      type="text"
                      value={chequeFrom}
                      onChange={(e) => {
                        setChequeFrom(e.target.value);
                        if (stopFieldErrors.chequeFrom)
                          setStopFieldErrors((p) => ({
                            ...p,
                            chequeFrom: undefined,
                          }));
                      }}
                      placeholder="Enter start number"
                      aria-required="true"
                      aria-invalid={!!stopFieldErrors.chequeFrom}
                      aria-describedby={
                        stopFieldErrors.chequeFrom
                          ? 'cheque-from-error'
                          : undefined
                      }
                      className={`border rounded-md px-4 py-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] w-full focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-transparent ${
                        stopFieldErrors.chequeFrom
                          ? 'border-[#dc2626]'
                          : 'border-[#c4c4c4]'
                      }`}
                    />
                    {stopFieldErrors.chequeFrom && (
                      <p
                        id="cheque-from-error"
                        role="alert"
                        className="mt-2 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
                      >
                        {stopFieldErrors.chequeFrom}
                      </p>
                    )}
                  </div>
                  <div>
                    <label
                      htmlFor="cheque-to"
                      className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] block mb-2"
                    >
                      To (inclusive)
                    </label>
                    <input
                      id="cheque-to"
                      type="text"
                      value={chequeTo}
                      onChange={(e) => {
                        setChequeTo(e.target.value);
                        if (stopFieldErrors.chequeTo)
                          setStopFieldErrors((p) => ({
                            ...p,
                            chequeTo: undefined,
                          }));
                      }}
                      placeholder="Enter end number"
                      aria-required="true"
                      aria-invalid={!!stopFieldErrors.chequeTo}
                      aria-describedby={
                        stopFieldErrors.chequeTo ? 'cheque-to-error' : undefined
                      }
                      className={`border rounded-md px-4 py-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] w-full focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-transparent ${
                        stopFieldErrors.chequeTo
                          ? 'border-[#dc2626]'
                          : 'border-[#c4c4c4]'
                      }`}
                    />
                    {stopFieldErrors.chequeTo && (
                      <p
                        id="cheque-to-error"
                        role="alert"
                        className="mt-2 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
                      >
                        {stopFieldErrors.chequeTo}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Cheque amount (optional, single only). */}
            {stopType === 'single' && (
              <div>
                <label
                  htmlFor="cheque-amount"
                  className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] block mb-2"
                >
                  Cheque amount (if known)
                </label>
                <input
                  id="cheque-amount"
                  type="text"
                  value={chequeAmount}
                  onChange={(e) => setChequeAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="border border-[#c4c4c4] rounded-md px-4 py-3 text-[16px] font-['Arial',sans-serif] text-[#1d252c] placeholder:text-[#9ca3af] w-[350px] focus:outline-none focus:ring-2 focus:ring-[#005fcc] focus:border-transparent"
                />
              </div>
            )}

            {/* Reason for stopping. */}
            <div className="w-[350px]">
              <Dropdown
                id="stop-reason"
                label="Reason for stopping"
                placeholder="Select reason"
                value={reason}
                onChange={(value) => {
                  setReason(value);
                  // Clear the mandatory-field warning once the agent selects a
                  // reason (#607) — recoverable inline error, never sticky.
                  if (stopFieldErrors.reason)
                    setStopFieldErrors((p) => ({ ...p, reason: undefined }));
                }}
                options={STOP_REASONS}
              />
              {stopFieldErrors.reason && (
                <p
                  id="stop-reason-error"
                  role="alert"
                  className="mt-2 text-[14px] font-['Arial',sans-serif] text-[#dc2626]"
                >
                  {stopFieldErrors.reason}
                </p>
              )}
            </div>

            {/* Charge Yes/No. */}
            <fieldset className="border-0 p-0 m-0">
              <legend className="text-[16px] font-['Arial',sans-serif] font-bold text-[#1d252c] mb-2 p-0">
                Charge
              </legend>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="charge"
                    value="Yes"
                    checked={charge === 'Yes'}
                    onChange={() => setCharge('Yes')}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                    Yes
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="charge"
                    value="No"
                    checked={charge === 'No'}
                    onChange={() => setCharge('No')}
                    className="size-[20px] accent-[#FF703E] cursor-pointer"
                  />
                  <span className="text-[16px] font-['Arial',sans-serif] text-[#1d252c]">
                    No
                  </span>
                </label>
              </div>
            </fieldset>

            {/* Scripting + Stop cheque (CCF-37/78). Wired to `/accounts/
                cheque-stop` (#147); the gateway outcome is surfaced inline
                beside the action (#80/#82/#94). */}
            {charge && (
              <div className="border-t border-[#dad7d3] pt-6">
                {charge === 'Yes' && (
                  <>
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-2">
                      There is a charge of €5 to process this request.
                    </p>
                    <p className="text-[14px] font-['Arial',sans-serif] text-[#1d252c] mb-4">
                      All details above must be confirmed with the customer
                      before proceeding.
                    </p>
                    <div className="mb-6 flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="stop-scripting"
                        checked={stopScripting}
                        onChange={() => setStopScripting((c) => !c)}
                        className="size-[20px] accent-[#FF703E] cursor-pointer"
                      />
                      <label
                        htmlFor="stop-scripting"
                        className="text-[16px] font-['Arial',sans-serif] text-[#1d252c] cursor-pointer select-none"
                      >
                        Scripting complete
                      </label>
                    </div>
                    {stopScripting && (
                      <button
                        type="button"
                        onClick={() => void onStopCheque()}
                        disabled={stopping || selectedAccountMultiSignatory}
                        title={
                          selectedAccountMultiSignatory
                            ? MULTIPLE_SIGNATORIES_MESSAGE
                            : undefined
                        }
                        className="bg-[#FF703E] text-[#1d252c] px-8 py-3 rounded-[4px] text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {stopping ? 'Stopping…' : 'Stop cheque'}
                      </button>
                    )}
                  </>
                )}

                {charge === 'No' && (
                  <button
                    type="button"
                    onClick={() => void onStopCheque()}
                    disabled={stopping || selectedAccountMultiSignatory}
                    title={
                      selectedAccountMultiSignatory
                        ? MULTIPLE_SIGNATORIES_MESSAGE
                        : undefined
                    }
                    className="bg-[#FF703E] text-[#1d252c] px-8 py-3 rounded-[4px] text-[16px] font-['Arial',sans-serif] font-bold hover:bg-[#e55a2a] focus:outline-none focus:ring-4 focus:ring-offset-2 focus:ring-[#005fcc] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {stopping ? 'Stopping…' : 'Stop cheque'}
                  </button>
                )}

                {/* Inline outcome beside the action (#80/#82/#94). */}
                {stopError && (
                  <div
                    role="alert"
                    className="mt-4 p-4 bg-white border border-[#dc2626] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
                  >
                    {stopError}
                  </div>
                )}
                {stopSuccess && (
                  <div
                    role="status"
                    className="mt-4 p-4 bg-white border border-[#16a34a] font-['Arial',sans-serif] text-[14px] text-[#1d252c] max-w-[600px]"
                  >
                    {stopSuccess}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </main>
  );
}
